/**
 * 근태 › 사용자 관리 — 좌: 부서·팀 / 우: 사용자 정보 (2026-08-25 신규).
 *
 * **관리 콘솔(8444)의 `sections/UserWorkspace.tsx` 를 그대로 옮겨 온 화면이다.** 사용자 지시로
 * 콘솔의 사용자 관리를 근태 하위 메뉴에도 둔다 — 콘솔 쪽은 한 줄도 건드리지 않았고(그대로 남는다),
 * 여기 것은 복사본이다. 두 화면은 **같은 서버 라우트**를 부르므로 동작이 갈리지 않는다.
 *
 * 원본과 다른 곳은 셋뿐이다:
 *   1. 로그인한 사람을 `adminAuthStore`(콘솔 세션) 가 아니라 `authStore`(8443 세션) 에서 읽는다.
 *   2. API 는 옮겨 온 `api/admin-org.ts` · `api/admin-user.ts` 를 쓴다(경로 문자열은 동일).
 *   3. 회사명은 콘솔의 `/admin/institution`(BFF) 대신 원본 라우트 `/institution` 에서 읽는다.
 *
 * 시안 SSOT: docs/user-management-sample-v2.html
 *
 * 서버 SSOT (chat-server — 서버·마이그레이션 변경 0)
 *  - 조직  : /api/v1/admin/org/{divisions,teams,teams/:id/members}  (api/admin-org.ts)
 *  - 사용자: /api/v1/users (목록), /api/v1/admin/users/:id (상세·수정·비번), /role (권한)
 *
 * 권한(2026-08-25) — 관리자·시스템관리자도 이 화면에서 등록·지정하고 비밀번호를 바꾼다.
 *   무엇을 고를 수 있는지는 **로그인한 사람의 등급**이 정한다(lib/role-rank.ts):
 *   자기 등급보다 높은 권한은 목록에 나오지 않고, 그런 계정의 행은 손대지 못한다.
 *   화면은 안내만 하고 최종 판정은 서버가 한다(ROLE_ESCALATION_DENIED 등).
 *
 * ⚠ 소속의 진실은 **팀 멤버십 하나뿐**이다. 사용자→부서 직결 컬럼이 없으므로
 *   부서 노드는 "하위 팀 인원의 합계"이고, **어느 팀에도 없으면 «회사 직속»**이다
 *   (2026-08-31 사용자 결정 — 「미배정은 회사 아래로 포함된다」. 대표·시스템 계정의 제자리다).
 *   시안의 인사 항목(고용형태·입사일·직급·급여·발령이력)은 아직 스키마에 없어 넣지 않았다.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent } from 'react';
import { JobGradeSelect } from '../../components/JobGradeSelect';
import './UserManage.css';
import {
  Users, UserPlus, Building2, Search, Plus, ChevronRight, MoreVertical, X,
  Key, RotateCcw, Copy, Eye, EyeOff, AlertTriangle, Check, UserCheck,
} from '../../icons';
import { useAuthStore } from '../../store/authStore';
// 8443 은 화면 글자를 언어 설정에 맞춘다 — 콘솔 원본의 하드코딩 'ko-KR' 을 여기서만 바꿨다
// (`lib/i18n-locale-hardcode.test.ts` 가 하드코딩 로케일을 금지한다).
import { formatDateTime } from '../../lib/base-i18n';
import {
  assignableRolesFor, canManageRole, isAdminRole, ROLE_OPTION_LABEL, ROLE_ORDER, type ManagedRole,
} from '../../lib/role-rank';
import { StyledSelect, type SelectOption } from './UserManageSelect';
// 2026-08-25 — 회사 메뉴 · 부서/팀/개인 메뉴 권한. 좌측에서 고른 것이 곧 대상이다.
import {
  listDivisions, listTeams, createDivision, createTeam, updateDivision, updateTeam,
  deleteDivision, deleteTeam, listTeamMembers, addTeamMember, removeTeamMember, setTeamMemberLeader,
  getOrgDependents, transferOrg,
  type OrgDivision, type OrgTeam, type OrgDeleteResult, type OrgDependents,
} from '../../api/admin-org';
// 대표·부서장은 **역할이 아니라 지정**이다 — 이미 있는 라우트에서 읽어 표식으로만 보여 준다.
import { getOrgLeaders, saveOrgLeaders } from '../../api/hr-org-leader';
import {
  listUsers, getInstitution,
  changeUserRole, createUser, getAdminUserDetail, updateAdminUserProfile,
  setAdminUserPassword, resetAdminUserPassword,
  type UserListItem, type AssignableRole, type PasswordMode, type AdminUserDetail, type CreateUserResponse,
} from '../../api/admin-user';
// 사람 차례 — **직책 → 직위 → 이름** (2026-08-31 · 사용자 지시: 「대표가 젤 위로」).
import { DUTY_ORDER, sortPeople, type PeopleOrderContext } from '../../lib/people-order';
import { loadJobGradeLevels, levelOfPosition } from '../../lib/job-grade-levels';

/* ─────────────────────────── 상수 ─────────────────────────── */

const ROLE_LABEL: Record<string, string> = {
  SYSTEM_ADMIN: '시스템관리자', ADMIN: '관리자', MANAGER: '매니저', STAFF: '직원',
};

/**
 * ★★ 회사 화면에서 **고를 수 있는** 권한 — 관리자 · 직원 둘뿐이다 (2026-08-25 사용자 지시).
 *
 * 시스템관리자·매니저는 **관리 콘솔의 계정 관리**가 쓰는 계정 등급이라 여기 목록에 없다.
 * 위 `ROLE_LABEL` 은 **표시용**이라 넷을 그대로 둔다 — 이미 그 등급인 사람이 있으면
 * 행에도 셀렉트에도 제 이름으로 보여야 한다(빼면 "권한 없음" 처럼 읽힌다).
 *
 * ⚠ **대표는 역할이 아니라 지정이다** — `institution.representative_user_id` 를 가리키는
 *   포인터이고, 정하는 자리는 근태 › 대표·부서장(`/erp/hr/org-leaders`)이다.
 *   그래서 셀렉트가 아니라 **행의 표식**으로 보여 준다(부서장도 같다).
 */
const COMPANY_ASSIGNABLE_ROLES: ManagedRole[] = ['ADMIN', 'STAFF'];
const ROLE_RANK: Record<string, number> = { SYSTEM_ADMIN: 0, ADMIN: 1, MANAGER: 2, STAFF: 3 };

/**
 * ★★★ **계정 상태는 `isActive` 하나다** (2026-08-31 운영 실측으로 드러났다).
 *
 *    종전에는 `u.status` 를 「계정 상태」로 읽어 `active | invited | disabled | deleted` 로
 *    갈랐다. **그런 값은 어디에도 없다** — DB 의 `UserStatus` 는 `online | away | offline`
 *    (**접속 상태**)이고 목록·상세 둘 다 그 값을 준다. 운영에서 벌어진 일:
 *      · 칩 셋이 전부 **0** (전체 11 · 활성 0 · 초대됨 0 · 비활성 0)
 *      · **11행 전부 흐림**(`is-off`) — 모두가 비활성으로 보였다
 *      · 배지에 정의 없는 클래스 `um-st--offline` 과 **영문 "offline"** 이 그대로 노출
 *
 * ★ 계정이 살아 있는지는 **`isActive`** 가 답한다(목록·상세 둘 다 준다).
 * ⚠ **「초대됨」은 목록으로 알 수 없다** — 「비밀번호를 아직 안 정했다」(`hasPassword`)는
 *   상세에만 있고, 그것을 전 직원이 부르는 `/users` 에 실으면 **누구를 노리면 되는지**가
 *   모두에게 보인다. 그래서 칩을 지웠다(있어 봐야 늘 0 이었다). 한 사람의 초대 상태는
 *   드로어의 **비밀번호 탭**이 말한다.
 */
const STATUS_SEG: Array<{ key: string; label: string }> = [
  { key: 'all', label: '전체' },
  { key: 'on', label: '활성' },
  { key: 'off', label: '비활성' },
];

/** 계정이 살아 있는가 — 옛 서버가 칸을 안 주면 «살아 있다»로 본다(종전 총원과 같아진다). */
function isLive(u: { isActive?: boolean }): boolean { return u.isActive !== false; }

type SortKey = 'employeeNumber' | 'name' | 'email' | 'role';
const SORT_OPTS: Array<{ value: string; label: string }> = [
  { value: 'employeeNumber:asc', label: '사번 오름차순' },
  { value: 'employeeNumber:desc', label: '사번 내림차순' },
  { value: 'name:asc', label: '이름 가나다' },
  { value: 'email:asc', label: '이메일 순' },
  { value: 'role:asc', label: '권한 순' },
];

/** 부서 식별색 — tokens.css 의 accent 팔레트("분류 식별" 용도로 이미 정의됨). */
const HUES = [
  'var(--accent-blue)', 'var(--accent-purple)', 'var(--accent-orange)',
  'var(--accent-pink)', 'var(--accent-green)', 'var(--accent-red)',
];
function hueOf(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return HUES[h % HUES.length]!;
}
function hueStyle(hue: string): CSSProperties {
  return { '--um-hue': hue } as CSSProperties;
}

const PAGE_SIZE = 20;

/** 서버 JSON schema 상한 (admin-org.routes.ts). */
const SORT_MAX = 9999;

/** '부서 없음' 셀렉트 값 — 빈 문자열은 StyledSelect 의 placeholder 와 헷갈린다. */
const NO_DIVISION = '__none__';

const ERR_MAP: Record<string, string> = {
  EMAIL_INVALID: 'email 형식이 올바르지 않습니다.',
  EMAIL_DOMAIN_MISMATCH: 'email 도메인이 회사 도메인과 일치해야 합니다.',
  EMAIL_EXISTS: '이미 사용 중인 email 입니다.',
  EMPLOYEE_NUMBER_EXISTS: '이미 사용 중인 사번입니다.',
  EMPLOYEE_NUMBER_REQUIRED: '임시 비밀번호는 사번이 필요합니다. 먼저 정보 탭에서 사번을 저장하세요.',
  PASSWORD_POLICY_VIOLATED: '비밀번호 정책 위반 — 8자 이상, 영문·숫자·특수문자 3종 이상.',
  CANNOT_CHANGE_OWN_PASSWORD: '본인 비밀번호는 내 계정 설정에서 변경하세요.',
  CANNOT_CHANGE_ADMIN_PASSWORD: '관리자 계정의 비밀번호는 이 화면에서 변경할 수 없습니다.',
  CANNOT_CHANGE_HIGHER_ROLE_PASSWORD: '자신보다 상위 권한 계정의 비밀번호는 변경할 수 없습니다.',
  CANNOT_MANAGE_HIGHER_ROLE: '자신보다 상위 권한 계정의 권한은 변경할 수 없습니다.',
  ROLE_ESCALATION_DENIED: '자신보다 상위 권한은 부여할 수 없습니다.',
  LAST_ADMIN: '회사의 마지막 관리자 계정입니다. 다른 관리자를 먼저 지정하세요.',
  CANNOT_CHANGE_ADMIN_EMAIL: '다른 관리자 계정의 이메일은 변경할 수 없습니다.',
  CANNOT_CHANGE_OWN_ACTIVE: '본인 계정의 활성 상태는 변경할 수 없습니다.',
  CANNOT_CHANGE_ADMIN_ACTIVE: '관리자 계정의 활성 상태는 변경할 수 없습니다.',
  CANNOT_MODIFY_BOT: 'AI 봇 계정은 수정할 수 없습니다.',
  CANNOT_CHANGE_OWN_ROLE: '본인의 권한은 변경할 수 없습니다.',
  CANNOT_DEMOTE_ADMIN: '관리자(ADMIN) 권한은 이 화면에서 변경할 수 없습니다.',
  ORG_HAS_DEPENDENTS: '연관 데이터가 있어 삭제할 수 없습니다. 먼저 다른 조직으로 이관하세요.',
  TRANSFER_TARGET_MUST_BE_TEAM: '워크스페이스 공유·용량·파일은 팀만 가리킬 수 있습니다. 이관 대상을 팀으로 고르세요.',
  TRANSFER_TARGET_INVALID: '자기 자신이나 하위 조직으로는 이관할 수 없습니다.',
  TRANSFER_TARGET_NOT_FOUND: '이관 대상 조직을 찾을 수 없습니다.',
  USER_NOT_FOUND: '사용자를 찾을 수 없습니다.',
  CONFLICT: '다른 작업과 충돌했습니다. 다시 시도하세요.',
};
function errMsg(e: unknown, fallback: string): string {
  const r = e as { response?: { data?: { code?: string; message?: string; error?: string; violations?: string[] } }; message?: string };
  const d = r?.response?.data;
  const mapped = d?.code ? ERR_MAP[d.code] : undefined;
  const v = Array.isArray(d?.violations) && d.violations.length ? ` (${d.violations.join(', ')})` : '';
  return `${mapped ?? d?.message ?? d?.error ?? r?.message ?? fallback}${v}`;
}

/* ─────────────────────────── 유틸 ─────────────────────────── */

/**
 * ★ `joinedAt` 은 **이 팀에 들어온 날**이다 — 「팀장이 «된» 날」이 아니다(서버에 그 칸이 없다).
 *   조직도 상세가 그대로 그렇게 적는다. 없는 값을 그럴듯한 이름으로 적으면 아무도 못 고친다.
 */
interface Membership { teamId: string; isLeader: boolean; joinedAt?: string }
type Sel =
  | { kind: 'all' }
  | { kind: 'none' }
  | { kind: 'div'; id: string }
  | { kind: 'team'; id: string };

/** 부서·팀 정렬 — 서버와 같은 규칙(sortOrder asc)에 이름을 2차 키로 얹는다. */
function bySortOrder<T extends { sortOrder: number; name: string }>(a: T, b: T): number {
  return (a.sortOrder - b.sortOrder) || a.name.localeCompare(b.name, 'ko');
}

/** 형제 중 최댓값+1 — 서버가 자동 채번을 하지 않아(미전송 시 0) 화면이 정해 준다. */
function nextSortOrder(siblings: Array<{ sortOrder: number }>): number {
  return Math.min(SORT_MAX, siblings.reduce((m, x) => Math.max(m, x.sortOrder ?? 0), -1) + 1);
}

function initial(name: string, email: string) {
  return (name || email || '?').trim().charAt(0).toUpperCase();
}

/** 동시 요청 상한을 둔 map — 팀 수만큼 멤버 조회가 나가므로 서버를 몰아치지 않는다. */
async function mapPool<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const out = new Array<R>(items.length);
  let cursor = 0;
  async function worker() {
    for (;;) {
      const i = cursor++;
      if (i >= items.length) return;
      out[i] = await fn(items[i]!);
    }
  }
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker));
  return out;
}

async function loadAllUsers(): Promise<UserListItem[]> {
  const acc: UserListItem[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < 20; page++) {
    const r = await listUsers({ limit: 100, ...(cursor ? { cursor } : {}) });
    acc.push(...r.users);
    cursor = r.nextCursor;
    if (!cursor) break;
  }
  return acc;
}

/* ═══════════════════════════ 본체 ═══════════════════════════ */

/**
 * ★★ **한 화면이 두 얼굴을 갖는다** (2026-08-28 · 자리 재정의 판 4).
 *
 *   - `accounts` — 통합설정 › 사용자 › **사용자 관리**. **계정만** 다룬다:
 *     이름 · 계정명 · 비밀번호 · 상태 · 역할. 직위 · 소속 배정 · 조직도 트리는 여기 없다.
 *   - `org` — 통합설정 › 조직 › **조직도**. 회사의 뼈대: 부서·팀 만들기 · 이름 · 팀장 지정.
 *
 * ★ **코드를 옮기지 않고 «무엇을 그리는가» 만 가른다.** 1,700줄을 쪼개는 것은 그 자체로
 *   회귀 위험이고, 두 화면이 같은 목록·같은 조직 데이터를 쓰기 때문에 가르는 선은 화면이지
 *   모듈이 아니다.
 *
 * ★★ **왜 계정만인가** — 계정을 만드는 일과 사람의 인사 정보를 넣는 일은 **다른 사람의 일**이다.
 *   섞여 있으면 계정 담당자가 직위를 찍고 인사 담당자가 다시 고친다. 계정을 만들면
 *   **인사에서 이어받는다**(인사 홈의 «인사 정보 없는 사람» 이 그 큐다).
 */
export interface UserManageProps {
  /** 기본은 계정만. 조직도 화면이 `org` 로 부른다. */
  mode?: 'accounts' | 'org';
}

export function UserManageWorkspace({ mode = 'accounts' }: UserManageProps = {}) {
  const isOrgMode = mode === 'org';
  const [divs, setDivs] = useState<OrgDivision[]>([]);
  const [teams, setTeams] = useState<OrgTeam[]>([]);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [members, setMembers] = useState<Record<string, Membership[]>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; tone: 'ok' | 'warn' } | null>(null);
  /** 트리 루트에 쓸 회사명. 못 받아오면 빈 문자열 → '전체 사용자' 로 폴백. */
  const [company, setCompany] = useState('');
  /** 대표 1명 · 부서장 여럿의 userId. 조회 실패(옛 서버·권한)는 표식만 안 붙는다. */
  const [repId, setRepId] = useState<string | null>(null);
  const [divLeaders, setDivLeaders] = useState<Set<string>>(() => new Set());
  /**
   * 부서 → 부서장 userId. 위 `divLeaders` 는 **행에 표식을 붙이는** 용도라 어느 부서의 장인지
   * 모른다 — 조직도 상세는 「이 부서의 장이 누구인가」를 물으므로 짝을 따로 든다.
   */
  const [divLeaderOf, setDivLeaderOf] = useState<Map<string, string | null>>(() => new Map());

  const [sel, setSel] = useState<Sel>({ kind: 'all' });
  const [openDivs, setOpenDivs] = useState<Record<string, boolean>>({});
  const [orgQ, setOrgQ] = useState('');
  /** 생성 다이얼로그 — 종류·이름·상위 부서를 한 폼에서 받는다. */
  const [mk, setMk] = useState<{ kind: 'div' | 'team'; name: string; divisionId: string } | null>(null);
  const [renaming, setRenaming] = useState<{ kind: 'div' | 'team'; id: string } | null>(null);
  const [renameName, setRenameName] = useState('');
  const [orgBusy, setOrgBusy] = useState<string | null>(null);
  /** 삭제하려는데 연관 데이터가 있을 때 뜨는 이관 다이얼로그. */
  const [xfer, setXfer] = useState<{
    kind: 'div' | 'team'; id: string; name: string;
    dep: OrgDependents; toId: string; busy: boolean;
  } | null>(null);

  /**
   * ★ 오른쪽 「메뉴 권한」 탭과 그 대상 계산은 **뺐다** (2026-08-28 · 판 4).
   *   메뉴 권한은 통합설정 › 조직 › 메뉴 권한이 갖는다 — 거기서는 트리에 없던
   *   «부서·팀 없는 직원» 도 대상이 되고, 끄는 순서 경고가 화면에 함께 선다.
   */
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [roleFilter, setRoleFilter] = useState('');
  const [sort, setSort] = useState('employeeNumber:asc');
  const [page, setPage] = useState(1);
  const [picked, setPicked] = useState<Record<string, true>>({});
  const [bulkTeam, setBulkTeam] = useState('');
  const [bulkRole, setBulkRole] = useState<AssignableRole>('STAFF');
  // 내 등급 — 고를 수 있는 권한과 손댈 수 있는 행을 가른다(서버가 최종 판정).
  const me = useAuthStore((st) => st.user);
  const myRole = me?.role ?? '';
  /**
   * 8443 세션의 `id` 는 number 다(콘솔 세션은 string). 서버가 사용자 id 를 **문자열**로 주므로
   * 비교·전달 전에 문자열로 맞춘다 — 원본(콘솔)과 다른 유일한 지점이라 여기서 한 번만 변환한다.
   */
  const myId = me?.id != null ? String(me.id) : null;
  // 일괄 변경도 회사 화면이 다루는 권한만 — 콘솔 등급은 계정 관리에서 준다.
  const myRoleOptions = useMemo(
    () => assignableRolesFor(myRole).filter((r) => COMPANY_ASSIGNABLE_ROLES.includes(r)),
    [myRole],
  );
  const [bulkBusy, setBulkBusy] = useState(false);

  /* ── 조직도 상세 (2026-08-31 · 시안 맞춤 6/9) ──────────────────────────────
     고른 조직 하나의 이름·소속 부서·정렬 순서를 오른쪽에서 고친다. 저장은 PATCH 한 번이다. */
  const [odForm, setOdForm] = useState<{ name: string; divisionId: string; sortOrder: string }>(
    { name: '', divisionId: NO_DIVISION, sortOrder: '0' },
  );
  const [odBusy, setOdBusy] = useState(false);
  /** 팀장·부서장으로 세울 사람 — 그 조직의 인원 중에서 고른다. */
  const [leadPick, setLeadPick] = useState('');
  /** 팀원 추가 — 이 팀 밖의 사람을 찾아 넣는다. */
  const [addMem, setAddMem] = useState<{ teamId: string; q: string } | null>(null);

  const [menu, setMenu] = useState<{ x: number; y: number; kind: 'org' | 'user'; id: string } | null>(null);
  const [drawer, setDrawer] = useState<{ mode: 'edit'; userId: string } | { mode: 'new' } | null>(null);

  const dragRef = useRef<{ type: 'team' | 'user'; ids: string[] } | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  /* ── 로딩 ── */
  const reload = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      // 회사명은 실패해도 화면을 막지 않는다 — 루트 라벨만 폴백된다.
      const [d, t, inst] = await Promise.all([
        listDivisions(),
        listTeams(),
        getInstitution().catch(() => null),
      ]);
      const u = await loadAllUsers();
      const lists = await mapPool(t, 4, (team) => listTeamMembers(team.id).catch(() => []));
      const map: Record<string, Membership[]> = {};
      t.forEach((team, i) => {
        (lists[i] ?? []).forEach((m) => {
          (map[m.user.id] ??= []).push({ teamId: team.id, isLeader: m.isLeader, joinedAt: m.joinedAt });
        });
      });
      setDivs(d); setTeams(t); setUsers(u); setMembers(map); setCompany(inst?.name ?? '');
      // 대표·부서장 표식 — 실패해도 화면을 막지 않는다(표식만 안 붙는다).
      getOrgLeaders()
        .then((L) => {
          setRepId(L.representative?.userId ?? null);
          setDivLeaders(new Set(L.divisions.map((x) => x.leaderUserId).filter((v): v is string => !!v)));
          setDivLeaderOf(new Map(L.divisions.map((x) => [x.id, x.leaderUserId])));
        })
        .catch(() => { setRepId(null); setDivLeaders(new Set()); setDivLeaderOf(new Map()); });
      setOpenDivs((prev) => {
        const next = { ...prev };
        d.forEach((x) => { if (next[x.id] === undefined) next[x.id] = true; });
        return next;
      });
    } catch (e) {
      setErr(errMsg(e, '조직·사용자 정보를 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), 5000);
    return () => window.clearTimeout(t);
  }, [notice]);

  const mkOpen = mk !== null;
  const xferOpen = xfer !== null;
  const addMemOpen = addMem !== null;
  useEffect(() => {
    if (!mkOpen && !xferOpen && !addMemOpen) return;
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') { setMk(null); setXfer(null); setAddMem(null); } }
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [mkOpen, xferOpen, addMemOpen]);

  useEffect(() => {
    if (!menu) return;
    function close() { setMenu(null); }
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => { window.removeEventListener('resize', close); window.removeEventListener('scroll', close, true); };
  }, [menu]);

  /* ── 파생 ── */
  const teamById = useMemo(() => {
    const m = new Map<string, OrgTeam>();
    teams.forEach((t) => m.set(t.id, t));
    return m;
  }, [teams]);
  const divById = useMemo(() => {
    const m = new Map<string, OrgDivision>();
    divs.forEach((d) => m.set(d.id, d));
    return m;
  }, [divs]);

  const sortedDivs = useMemo(() => [...divs].sort(bySortOrder), [divs]);
  const teamsOfDiv = useCallback(
    (id: string) => teams.filter((t) => t.divisionId === id).sort(bySortOrder),
    [teams],
  );
  const rootTeams = useMemo(() => teams.filter((t) => !t.divisionId).sort(bySortOrder), [teams]);

  const divisionOptions = useMemo<SelectOption[]>(() => [
    { value: NO_DIVISION, label: '부서 없음 (회사 바로 아래)' },
    ...sortedDivs.map((d) => ({ value: d.id, label: d.isActive ? d.name : `${d.name} (비활성)` })),
  ], [sortedDivs]);

  const teamHue = useCallback((t: OrgTeam) => hueOf(t.divisionId ?? t.id), []);

  const countByTeam = useMemo(() => {
    const c = new Map<string, number>();
    Object.values(members).forEach((list) => list.forEach((m) => c.set(m.teamId, (c.get(m.teamId) ?? 0) + 1)));
    return c;
  }, [members]);
  const countOfDiv = useCallback(
    (id: string) => teamsOfDiv(id).reduce((s, t) => s + (countByTeam.get(t.id) ?? 0), 0),
    [teamsOfDiv, countByTeam],
  );
  /**
   * 직위 급표 — 「직책 → **직위** → 이름」의 둘째 축(2026-08-31 · 사용자 지시).
   * ★ 못 읽으면 빈 표다(담당자만 볼 수 있는 목록이다) — 그때는 직위를 「모름」으로 쳐서
   *   이름순으로 내려앉을 뿐, 목록이 사라지지 않는다.
   */
  const [gradeLevels, setGradeLevels] = useState<ReadonlyMap<string, number>>(new Map());
  useEffect(() => {
    let alive = true;
    void loadJobGradeLevels().then((m) => { if (alive) setGradeLevels(m); });
    return () => { alive = false; };
  }, []);

  /**
   * ★★★ **사람 목록의 차례** (2026-08-31 · 사용자 지시 — 「대표가 젤 위로 올라와야 함」).
   *
   *    이름순은 조직을 못 보여 준다 — 회사 직속 셋이 「admin · AI Bot · 김철희」로 서면
   *    **맨 아래가 대표**라는 것을 화면 어디서도 알 수 없었다.
   * ★ 직책은 **화면이 이미 아는 것**으로 매긴다 — 대표(`repId`) · 부서장(`divLeaders`) ·
   *   팀장(`isLeader`). 새로 물어보는 곳은 직위 급표 하나뿐이다.
   */
  const orderCtx: PeopleOrderContext = useMemo(() => ({
    dutyOrderOf: (p) => {
      if (repId && p.id === repId) return DUTY_ORDER.REPRESENTATIVE;
      if (divLeaders.has(p.id)) return DUTY_ORDER.DIVISION_HEAD;
      if ((members[p.id] ?? []).some((m) => m.isLeader)) return DUTY_ORDER.TEAM_LEAD;
      return DUTY_ORDER.MEMBER;
    },
    levelOf: (position) => levelOfPosition(gradeLevels, position),
  }), [repId, divLeaders, members, gradeLevels]);

  const unassigned = useMemo(
    () => sortPeople(users.filter((u) => !(members[u.id]?.length)), orderCtx),
    [users, members, orderCtx],
  );

  const usersById = useMemo(() => {
    const m = new Map<string, UserListItem>();
    users.forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);

  /**
   * 팀장 — **사람과 합류일까지** 돌려준다 (2026-08-31 · 시안 6/9).
   * 종전에는 이름 한 글자만 돌려줬다(작은 카드에 「팀장: 홍길동」 한 줄이 전부였다).
   */
  const leaderEntryOfTeam = useCallback((teamId: string): { user: UserListItem; joinedAt?: string } | null => {
    for (const [uid, list] of Object.entries(members)) {
      const hit = list.find((m) => m.teamId === teamId && m.isLeader);
      if (!hit) continue;
      const u = usersById.get(uid);
      if (u) return { user: u, joinedAt: hit.joinedAt };
    }
    return null;
  }, [members, usersById]);

  /** 이 팀의 사람 — 팀장을 맨 앞에, 나머지는 가나다. */
  const membersOfTeam = useCallback((teamId: string): UserListItem[] => {
    const out: UserListItem[] = [];
    Object.entries(members).forEach(([uid, list]) => {
      if (!list.some((m) => m.teamId === teamId)) return;
      const u = usersById.get(uid);
      if (u) out.push(u);
    });
    /**
     * ★ **이 팀의 팀장**이 맨 위다 — 다른 팀에서 팀장이어도 이 목록에서는 팀원이다.
     *   (공통 차례의 「팀장」은 «어느 팀이든»이라, 팀 상세에서는 이 자리에서 좁힌다.)
     */
    const isLead = (u: UserListItem) => (members[u.id] ?? []).some((m) => m.teamId === teamId && m.isLeader);
    return sortPeople(out, {
      ...orderCtx,
      dutyOrderOf: (p) => (isLead(p as UserListItem)
        ? DUTY_ORDER.TEAM_LEAD
        : Math.max(orderCtx.dutyOrderOf(p), DUTY_ORDER.MEMBER)),
    });
  }, [members, usersById, orderCtx]);

  /**
   * 이 부서의 사람 — **하위 팀 인원의 합**이다.
   * ⚠ 사용자→부서 직결 컬럼이 없다(파일 머리 주석) — 팀에 안 든 사람은 부서에도 안 든다.
   */
  const membersOfDiv = useCallback((divId: string): UserListItem[] => {
    const ids = new Set(teamsOfDiv(divId).map((t) => t.id));
    const seen = new Set<string>();
    const out: UserListItem[] = [];
    Object.entries(members).forEach(([uid, list]) => {
      if (seen.has(uid) || !list.some((m) => ids.has(m.teamId))) return;
      const u = usersById.get(uid);
      if (u) { seen.add(uid); out.push(u); }
    });
    return sortPeople(out, orderCtx);
  }, [members, usersById, teamsOfDiv, orderCtx]);

  const scoped = useMemo(() => {
    if (sel.kind === 'all') return users;
    if (sel.kind === 'none') return unassigned;
    if (sel.kind === 'team') return users.filter((u) => (members[u.id] ?? []).some((m) => m.teamId === sel.id));
    const ids = new Set(teamsOfDiv(sel.id).map((t) => t.id));
    return users.filter((u) => (members[u.id] ?? []).some((m) => ids.has(m.teamId)));
  }, [sel, users, unassigned, members, teamsOfDiv]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const arr = scoped.filter((u) => {
      if (status !== 'all' && isLive(u) !== (status === 'on')) return false;
      if (roleFilter && u.role !== roleFilter) return false;
      if (s && !`${u.name} ${u.email} ${u.employeeNumber ?? ''}`.toLowerCase().includes(s)) return false;
      return true;
    });
    const [key, dir] = sort.split(':') as [SortKey, 'asc' | 'desc'];
    const sign = dir === 'desc' ? -1 : 1;
    arr.sort((a, b) => {
      let c: number;
      if (key === 'role') c = (ROLE_RANK[a.role] ?? 99) - (ROLE_RANK[b.role] ?? 99);
      else c = String(a[key] ?? '').toLowerCase().localeCompare(String(b[key] ?? '').toLowerCase(), 'ko', { numeric: true });
      if (c === 0) c = a.name.localeCompare(b.name, 'ko');
      return c * sign;
    });
    return arr;
  }, [scoped, q, status, roleFilter, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const curPage = Math.min(page, pageCount);
  const rows = filtered.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);
  const pickedIds = Object.keys(picked);

  useEffect(() => { setPage(1); setPicked({}); }, [sel, status, roleFilter, q]);

  /**
   * 고른 조직이 바뀌면 편집 칸을 그 조직의 값으로 되돌린다 (2026-08-31 · 시안 6/9).
   * `teamById`/`divById` 는 목록을 다시 읽었을 때만 바뀌므로, 타이핑 중에 발밑에서 지워지지 않는다.
   */
  useEffect(() => {
    setLeadPick('');
    if (sel.kind === 'team') {
      const t = teamById.get(sel.id);
      if (t) setOdForm({ name: t.name, divisionId: t.divisionId ?? NO_DIVISION, sortOrder: String(t.sortOrder ?? 0) });
      return;
    }
    if (sel.kind === 'div') {
      const d = divById.get(sel.id);
      if (d) setOdForm({ name: d.name, divisionId: NO_DIVISION, sortOrder: String(d.sortOrder ?? 0) });
    }
  }, [sel, teamById, divById]);

  /* ── 소속 표기 ── */
  function orgLabelOf(u: UserListItem): { text: string; hue: string; extra: number } {
    const list = members[u.id] ?? [];
    // ★★ 사람 행의 소속 칸도 조직도와 **같은 말**을 쓴다 (2026-08-31 · 사용자 지시).
    //    팀이 없다는 것은 «어디에도 없다»가 아니라 **회사에 바로 속한다**는 뜻이다.
    if (!list.length) return { text: '회사 직속', hue: 'var(--text-subtle)', extra: 0 };
    const primary = list.find((m) => m.isLeader) ?? list[0]!;
    const t = teamById.get(primary.teamId);
    if (!t) return { text: '회사 직속', hue: 'var(--text-subtle)', extra: 0 };
    const d = t.divisionId ? divById.get(t.divisionId) : null;
    return {
      text: d ? `${d.name} · ${t.name}` : t.name,
      hue: teamHue(t),
      extra: list.length - 1,
    };
  }
  function isLeaderOf(u: UserListItem): boolean {
    return (members[u.id] ?? []).some((m) => m.isLeader);
  }

  /* ── 조직 CRUD ── */
  async function submitMk() {
    if (!mk) return;
    const name = mk.name.trim();
    if (!name) return;
    setOrgBusy('mk'); setErr(null);
    try {
      if (mk.kind === 'div') {
        const d = await createDivision({ name, sortOrder: nextSortOrder(divs) });
        setNotice({ text: `부서 "${d.name}" 을(를) 만들었습니다.`, tone: 'ok' });
        setOpenDivs((p) => ({ ...p, [d.id]: true }));
        setSel({ kind: 'div', id: d.id });
      } else {
        const divisionId = mk.divisionId === NO_DIVISION ? null : mk.divisionId;
        const siblings = divisionId ? teamsOfDiv(divisionId) : rootTeams;
        const t = await createTeam({ divisionId, name, sortOrder: nextSortOrder(siblings) });
        setNotice({
          text: divisionId
            ? `${divById.get(divisionId)?.name ?? '부서'} 아래에 팀 "${t.name}" 을(를) 만들었습니다.`
            : `팀 "${t.name}" 을(를) 만들었습니다.`,
          tone: 'ok',
        });
        if (divisionId) setOpenDivs((p) => ({ ...p, [divisionId]: true }));
        setSel({ kind: 'team', id: t.id });
      }
      setMk(null);
      await reload();
    } catch (e) {
      setErr(errMsg(e, '조직 생성에 실패했습니다.'));
    } finally { setOrgBusy(null); }
  }

  /** 생성 다이얼로그 열기 — 팀이면 지금 보고 있는 부서를 상위로 미리 채운다. */
  function openMk(kind: 'div' | 'team') {
    const divId = sel.kind === 'div' ? sel.id : (sel.kind === 'team' ? teamById.get(sel.id)?.divisionId ?? null : null);
    setMk({ kind, name: '', divisionId: divId ?? NO_DIVISION });
  }

  async function submitRename() {
    if (!renaming) return;
    const name = renameName.trim();
    if (!name) { setRenaming(null); return; }
    setOrgBusy(renaming.id); setErr(null);
    try {
      if (renaming.kind === 'div') await updateDivision(renaming.id, { name });
      else await updateTeam(renaming.id, { name });
      setNotice({ text: `이름을 "${name}" 로 바꿨습니다.`, tone: 'ok' });
      setRenaming(null);
      await reload();
    } catch (e) {
      setErr(errMsg(e, '이름 변경에 실패했습니다.'));
    } finally { setOrgBusy(null); }
  }

  function describeDelete(r: OrgDeleteResult, kind: 'div' | 'team', name: string): { text: string; tone: 'ok' | 'warn' } {
    if (r.mode === 'deleted') {
      const extra = kind === 'div'
        ? (r.deletedTeams ? ` 하위 팀 ${r.deletedTeams}개도 함께 삭제됐습니다.` : '')
        : (r.removedMembers ? ` 팀원 배정 ${r.removedMembers}건이 정리됐습니다.` : '');
      return { text: `"${name}" 을(를) 삭제했습니다.${extra}`, tone: 'ok' };
    }
    const why = kind === 'div'
      ? `파일이 남아 있는 팀(${(r.teams ?? []).join(', ') || '일부'}) 때문에`
      : `이 팀이 소유한 파일 ${r.fileCount ?? 0}건 때문에`;
    return { text: `${why} "${name}" 을(를) 지우지 못하고 비활성 처리했습니다.`, tone: 'warn' };
  }

  async function removeOrg(kind: 'div' | 'team', id: string, name: string) {
    // 서버가 연관 데이터를 전수로 세어 준다(게시판·공유·열람자·워크스페이스 공유·용량·파일).
    // 하나라도 있으면 DELETE 가 409 를 주므로, 여기서 미리 물어보고 이관 다이얼로그를 띄운다.
    setOrgBusy(id); setErr(null);
    let dep: OrgDependents;
    try {
      dep = await getOrgDependents(kind, id);
    } catch (e) {
      setErr(errMsg(e, '연관 데이터를 확인하지 못했습니다.'));
      setOrgBusy(null);
      return;
    }
    setOrgBusy(null);

    if (dep.total > 0) {
      setXfer({ kind, id, name, dep, toId: '', busy: false });
      return;
    }

    const n = kind === 'div' ? countOfDiv(id) : (countByTeam.get(id) ?? 0);
    const kids = kind === 'div' ? teamsOfDiv(id).length : 0;
    const warn = [kids ? `하위 팀 ${kids}개` : '', n ? `소속 인원 ${n}명` : ''].filter(Boolean).join(' · ');
    if (!window.confirm(warn
      ? `"${name}" 에 ${warn}이(가) 있습니다. 그래도 삭제할까요?`
      : `"${name}" 을(를) 삭제할까요?`)) return;

    setOrgBusy(id);
    try {
      const r = kind === 'div' ? await deleteDivision(id) : await deleteTeam(id);
      setNotice(describeDelete(r, kind, name));
      setSel({ kind: 'all' });
      await reload();
    } catch (e) {
      setErr(errMsg(e, '삭제에 실패했습니다.'));
    } finally { setOrgBusy(null); }
  }

  /** 이관 → 삭제를 한 번에. 이관이 실패하면 삭제하지 않는다. */
  async function runTransfer() {
    if (!xfer || xfer.toId === '') return;
    const sep = xfer.toId.indexOf(':');
    const toType = xfer.toId.slice(0, sep) as 'team' | 'division';
    const toId = xfer.toId.slice(sep + 1);
    setXfer({ ...xfer, busy: true }); setErr(null);
    try {
      const moved = await transferOrg(xfer.kind, xfer.id, { toType, toId });
      const del = xfer.kind === 'div' ? await deleteDivision(xfer.id) : await deleteTeam(xfer.id);
      setNotice({
        text: `연관 데이터를 ${moved.to.name}(으)로 옮기고 "${xfer.name}" 을(를) 삭제했습니다.`,
        tone: del.mode === 'deleted' ? 'ok' : 'warn',
      });
      setXfer(null); setSel({ kind: 'all' });
      await reload();
    } catch (e) {
      setErr(errMsg(e, '이관에 실패했습니다. 삭제하지 않았습니다.'));
      setXfer((x) => (x ? { ...x, busy: false } : x));
    }
  }

  /** 삭제 없이 감추기 — 서버 DELETE 는 이제 연관 데이터가 있으면 거절하므로 이 경로가 대안이다. */
  async function toggleActive(kind: 'div' | 'team', id: string, next: boolean, name: string) {
    setOrgBusy(id); setErr(null);
    try {
      if (kind === 'div') await updateDivision(id, { isActive: next });
      else await updateTeam(id, { isActive: next });
      setNotice({ text: `"${name}" 을(를) ${next ? '활성' : '비활성'} 처리했습니다.`, tone: next ? 'ok' : 'warn' });
      await reload();
    } catch (e) {
      setErr(errMsg(e, '활성 상태 변경에 실패했습니다.'));
    } finally { setOrgBusy(null); }
  }

  async function moveTeam(teamId: string, divisionId: string | null) {
    const t = teamById.get(teamId);
    if (!t || t.divisionId === divisionId) return;
    setOrgBusy(teamId); setErr(null);
    try {
      await updateTeam(teamId, { divisionId });
      setNotice({
        text: divisionId
          ? `"${t.name}" 을(를) ${divById.get(divisionId)?.name ?? '부서'} 아래로 옮겼습니다.`
          : `"${t.name}" 을(를) 부서에서 분리했습니다. (divisionId = null)`,
        tone: 'ok',
      });
      if (divisionId) setOpenDivs((p) => ({ ...p, [divisionId]: true }));
      await reload();
    } catch (e) {
      setErr(errMsg(e, '팀 이동에 실패했습니다.'));
    } finally { setOrgBusy(null); }
  }

  /* ── 사용자 소속 이동 ── */
  async function moveUsersToTeam(userIds: string[], teamId: string) {
    const t = teamById.get(teamId);
    if (!t || !userIds.length) return;
    setBulkBusy(true); setErr(null);
    let ok = 0; let fail = 0;
    for (const uid of userIds) {
      try {
        for (const m of members[uid] ?? []) {
          if (m.teamId !== teamId) await removeTeamMember(m.teamId, uid);
        }
        if (!(members[uid] ?? []).some((m) => m.teamId === teamId)) await addTeamMember(teamId, { userId: uid });
        ok++;
      } catch { fail++; }
    }
    setNotice({
      text: `${t.name} 으로 ${ok}명을 옮겼습니다.${fail ? ` (실패 ${fail}명)` : ''}`,
      tone: fail ? 'warn' : 'ok',
    });
    setPicked({}); setBulkBusy(false);
    await reload();
  }

  async function detachUsers(userIds: string[]) {
    setBulkBusy(true); setErr(null);
    let ok = 0; let fail = 0;
    for (const uid of userIds) {
      try {
        for (const m of members[uid] ?? []) await removeTeamMember(m.teamId, uid);
        ok++;
      } catch { fail++; }
    }
    setNotice({ text: `${ok}명을 소속에서 제외했습니다.${fail ? ` (실패 ${fail}명)` : ''}`, tone: fail ? 'warn' : 'ok' });
    setPicked({}); setBulkBusy(false);
    await reload();
  }

  async function applyBulkRole() {
    const ids = pickedIds.filter((id) => {
      const u = users.find((x) => x.id === id);
      // 본인과 상위 권한 계정은 서버가 어차피 막는다 — 여기서 미리 빼 실패 건수를 만들지 않는다.
      return u && u.id !== myId && canManageRole(myRole, u.role);
    });
    if (!ids.length) { setErr('권한을 바꿀 수 있는 대상이 선택되지 않았습니다(본인·상위 권한 계정 제외).'); return; }
    setBulkBusy(true); setErr(null);
    const res = await Promise.allSettled(ids.map((id) => changeUserRole(id, bulkRole)));
    const ok = res.filter((r) => r.status === 'fulfilled').length;
    const fail = res.length - ok;
    setUsers((arr) => arr.map((u) => (ids.includes(u.id) ? { ...u, role: bulkRole } : u)));
    setNotice({
      text: `${ok}명의 권한을 ${ROLE_LABEL[bulkRole]}(으)로 바꿨습니다.${fail ? ` (실패 ${fail}명)` : ''}`,
      tone: fail ? 'warn' : 'ok',
    });
    setPicked({}); setBulkBusy(false);
  }

  async function toggleLeader(userId: string, teamId: string, next: boolean) {
    try {
      await setTeamMemberLeader(teamId, userId, next);
      setNotice({ text: next ? '팀장으로 지정했습니다.' : '팀장 지정을 해제했습니다.', tone: 'ok' });
      await reload();
    } catch (e) {
      setErr(errMsg(e, '팀장 지정에 실패했습니다.'));
    }
  }

  /* ── 조직도 상세 저장 (2026-08-31 · 시안 6/9) ── */

  /**
   * 이름 · 소속 부서 · 정렬 순서를 **한 번에** 저장한다. 서버가 셋을 한 PATCH 로 받는다
   * (`admin-org.routes.ts` teamPatchSchema) — 서버·마이그레이션 변경 0.
   */
  async function saveOdForm() {
    const name = odForm.name.trim();
    if (!name) { setErr('이름을 입력하세요.'); return; }
    // 서버 schema 상한이 있다 — 화면에서 먼저 잘라 400 을 만들지 않는다.
    const raw = Number(odForm.sortOrder);
    const sortOrder = Number.isFinite(raw) ? Math.max(0, Math.min(SORT_MAX, Math.trunc(raw))) : 0;
    setOdBusy(true); setErr(null);
    try {
      if (sel.kind === 'team') {
        await updateTeam(sel.id, {
          name, sortOrder,
          divisionId: odForm.divisionId === NO_DIVISION ? null : odForm.divisionId,
        });
      } else if (sel.kind === 'div') {
        await updateDivision(sel.id, { name, sortOrder });
      } else return;
      setNotice({ text: `"${name}" 을(를) 저장했습니다.`, tone: 'ok' });
      await reload();
    } catch (e) {
      setErr(errMsg(e, '저장에 실패했습니다.'));
    } finally { setOdBusy(false); }
  }

  /**
   * 부서장 — **부서마다 칸 하나**(`organization_divisions.leader_user_id`)라 새로 쓰면 옛 사람이
   * 저절로 밀린다. 팀장(`TeamMember.isLeader`)과 달리 「둘이 되는」 문제가 없다.
   * 부르는 곳은 근태 › 대표·부서장과 **같은 라우트**다 — 두 화면이 갈리지 않는다.
   */
  async function saveDivLeader(divId: string, userId: string | null) {
    setOdBusy(true); setErr(null);
    try {
      await saveOrgLeaders({ divisions: [{ id: divId, leaderUserId: userId }] });
      const L = await getOrgLeaders();
      setRepId(L.representative?.userId ?? null);
      setDivLeaders(new Set(L.divisions.map((x) => x.leaderUserId).filter((v): v is string => !!v)));
      setDivLeaderOf(new Map(L.divisions.map((x) => [x.id, x.leaderUserId])));
      setNotice({ text: userId ? '부서장을 지정했습니다.' : '부서장 지정을 해제했습니다.', tone: 'ok' });
      setLeadPick('');
    } catch (e) {
      setErr(errMsg(e, '부서장 지정에 실패했습니다.'));
    } finally { setOdBusy(false); }
  }

  /* ── 드래그 ── */
  function onDropNode(e: DragEvent, target: { kind: 'div'; id: string } | { kind: 'team'; id: string } | { kind: 'root' }) {
    const d = dragRef.current;
    setDropTarget(null);
    if (!d) return;
    if (d.type === 'team') {
      if (target.kind === 'div') { e.preventDefault(); void moveTeam(d.ids[0]!, target.id); }
      else if (target.kind === 'root') { e.preventDefault(); void moveTeam(d.ids[0]!, null); }
      return;
    }
    if (d.type === 'user' && target.kind === 'team') { e.preventDefault(); void moveUsersToTeam(d.ids, target.id); }
  }
  function canDrop(target: 'div' | 'team' | 'root'): boolean {
    const d = dragRef.current;
    if (!d) return false;
    if (d.type === 'team') return target === 'div' || target === 'root';
    return target === 'team';
  }

  /* ── 트리 렌더 ── */
  const matchQ = useCallback((name: string) => {
    const s = orgQ.trim().toLowerCase();
    return !s || name.toLowerCase().includes(s);
  }, [orgQ]);

  function isSel(kind: Sel['kind'], id?: string) {
    if (sel.kind !== kind) return false;
    if (kind === 'div' || kind === 'team') return (sel as { id: string }).id === id;
    return true;
  }

  function renderTeamNode(t: OrgTeam) {
    if (renaming?.kind === 'team' && renaming.id === t.id) {
      return (
        <div className="um-mkrow" key={t.id}>
          <input className="um-inp" value={renameName} autoFocus
            onChange={(e) => setRenameName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submitRename(); if (e.key === 'Escape') setRenaming(null); }} />
          <button type="button" className="um-btn um-btn--sm um-btn--primary" onClick={() => void submitRename()}>저장</button>
          <button type="button" className="um-btn um-btn--sm um-btn--ghost" onClick={() => setRenaming(null)}>취소</button>
        </div>
      );
    }
    const hue = teamHue(t);
    return (
      <div
        key={t.id}
        className={`um-node${isSel('team', t.id) ? ' is-sel' : ''}${dropTarget === `t:${t.id}` ? ' is-drop' : ''}${orgBusy === t.id ? ' is-busy' : ''}${t.isActive ? '' : ' is-inactive'}`}
        style={hueStyle(hue)}
        role="treeitem" aria-selected={isSel('team', t.id)} tabIndex={0}
        draggable
        onDragStart={() => { dragRef.current = { type: 'team', ids: [t.id] }; }}
        onDragEnd={() => { dragRef.current = null; setDropTarget(null); }}
        onDragOver={(e) => { if (canDrop('team')) { e.preventDefault(); setDropTarget(`t:${t.id}`); } }}
        onDragLeave={() => setDropTarget((p) => (p === `t:${t.id}` ? null : p))}
        onDrop={(e) => onDropNode(e, { kind: 'team', id: t.id })}
        onClick={() => setSel({ kind: 'team', id: t.id })}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSel({ kind: 'team', id: t.id }); } }}
      >
        <span className="um-tw is-leaf" />
        <span className="um-mk um-mk--team"><i /></span>
        <span className="um-node__nm">{t.name}</span>
        {!t.isActive && <span className="um-node__off">비활성</span>}
        <span className="um-node__n">{countByTeam.get(t.id) ?? 0}</span>
        <button type="button" className="um-node__mn" aria-label={`${t.name} 작업`}
          onClick={(e) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setMenu({ x: r.right, y: r.bottom, kind: 'org', id: `t:${t.id}` }); }}>
          <MoreVertical size={13} />
        </button>
      </div>
    );
  }

  function renderDivNode(d: OrgDivision) {
    const kids = teamsOfDiv(d.id);
    const open = openDivs[d.id] !== false;
    const hue = hueOf(d.id);
    if (renaming?.kind === 'div' && renaming.id === d.id) {
      return (
        <div className="um-mkrow" key={d.id} style={{ paddingLeft: 8 }}>
          <input className="um-inp" value={renameName} autoFocus
            onChange={(e) => setRenameName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submitRename(); if (e.key === 'Escape') setRenaming(null); }} />
          <button type="button" className="um-btn um-btn--sm um-btn--primary" onClick={() => void submitRename()}>저장</button>
          <button type="button" className="um-btn um-btn--sm um-btn--ghost" onClick={() => setRenaming(null)}>취소</button>
        </div>
      );
    }
    const visible = kids.filter((t) => matchQ(t.name) || matchQ(d.name));
    return (
      <div key={d.id}>
        <div
          className={`um-node${isSel('div', d.id) ? ' is-sel' : ''}${dropTarget === `d:${d.id}` ? ' is-drop' : ''}${orgBusy === d.id ? ' is-busy' : ''}${d.isActive ? '' : ' is-inactive'}`}
          style={hueStyle(hue)}
          role="treeitem" aria-selected={isSel('div', d.id)} aria-expanded={open} tabIndex={0}
          onDragOver={(e) => { if (canDrop('div')) { e.preventDefault(); setDropTarget(`d:${d.id}`); } }}
          onDragLeave={() => setDropTarget((p) => (p === `d:${d.id}` ? null : p))}
          onDrop={(e) => onDropNode(e, { kind: 'div', id: d.id })}
          onClick={() => setSel({ kind: 'div', id: d.id })}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSel({ kind: 'div', id: d.id }); } }}
        >
          <button type="button" className={`um-tw${open ? ' is-open' : ''}`} aria-label={open ? '접기' : '펼치기'}
            onClick={(e) => { e.stopPropagation(); setOpenDivs((p) => ({ ...p, [d.id]: !open })); }}>
            <ChevronRight size={11} />
          </button>
          <span className="um-mk um-mk--dept"><i /></span>
          <span className="um-node__nm">{d.name}</span>
          {!d.isActive && <span className="um-node__off">비활성</span>}
          <span className="um-node__n">{countOfDiv(d.id)}</span>
          <button type="button" className="um-node__mn" aria-label={`${d.name} 작업`}
            onClick={(e) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setMenu({ x: r.right, y: r.bottom, kind: 'org', id: `d:${d.id}` }); }}>
            <MoreVertical size={13} />
          </button>
        </div>
        {open && visible.length > 0 && <div className="um-kids">{visible.map(renderTeamNode)}</div>}
      </div>
    );
  }

  /* ── 조직도 상세 — 고른 조직 하나를 오른쪽에서 다 본다 (2026-08-31 · 시안 맞춤 6/9) ──
     ★★★ **조직을 손보러 왔는데 계정 목록이 먼저 보였다.** 종전 조직도는 왼쪽이 트리,
        오른쪽이 «계정 표»(상태 필터 · 권한 필터 · 페이저)였고, 조직 자체의 요약은 트리 아래
        작은 카드 한 장이 전부였다. 그래서 **팀장이 누구인지 · 팀원이 몇 명인지**를 이 화면에서
        볼 수 없었다. 시안이 오른쪽을 통째로 «고른 조직의 상세» 로 그린 이유가 그것이다.
     ★ **계정 표가 사라진 게 아니라 제 자리로 갔다** — 통합설정 › 사용자 › 사용자 관리.
        여기서 하던 일은 하나도 안 빠졌다: 이름 변경 · 부서 이동 · 정렬 · 활성 · 삭제 ·
        소속 이동(팀원 추가 · 미배정 일괄 배정) · 인사정보 열기(사람을 누르면 종전 드로어).
     ★ 못 읽어도 트리는 제 힘으로 산다 — 이 함수는 고른 것이 없으면 요약만 그린다. */

  /** 사람 한 명 — 누르면 종전 드로어(정보 · 소속 · 비밀번호)가 열린다. */
  function odChip(u: UserListItem, teamId: string | null) {
    const lead = teamId ? (members[u.id] ?? []).some((m) => m.teamId === teamId && m.isLeader) : false;
    const org = orgLabelOf(u);
    return (
      <button
        key={u.id} type="button"
        className={`um-chip${lead ? ' is-lead' : ''}${isLive(u) ? '' : ' is-off'}`}
        style={hueStyle(org.hue)}
        onClick={() => setDrawer({ mode: 'edit', userId: u.id })}
      >
        <span className="um-av" aria-hidden="true">{initial(u.name, u.email)}</span>
        <b>{u.name || '(이름 없음)'}</b>
        {u.position && <em>{u.position}</em>}
        {lead && <span className="um-tag um-tag--lead">팀장</span>}
        {repId === u.id && <span className="um-tag um-tag--lead">대표</span>}
        {!isLive(u) && <span className="um-chip__off">비활성</span>}
      </button>
    );
  }

  /** 저장 줄 — 고친 것이 없으면 눌리지 않는다(무엇이 바뀌었는지 화면이 말해 준다). */
  function odSaveRow(dirty: boolean, hint: string) {
    return (
      <div className="um-od__save">
        <button type="button" className="um-btn um-btn--sm um-btn--primary" disabled={!dirty || odBusy}
          onClick={() => void saveOdForm()}>{odBusy ? '저장 중…' : '저장'}</button>
        {dirty && (
          <button type="button" className="um-btn um-btn--sm um-btn--ghost" disabled={odBusy}
            onClick={() => setSel({ ...sel })}>되돌리기</button>
        )}
        <span className="um-f__hint">{dirty ? '고친 것이 있습니다 — 저장해야 반영됩니다.' : hint}</span>
      </div>
    );
  }

  function renderTeamPane(t: OrgTeam) {
    const list = membersOfTeam(t.id);
    const lead = leaderEntryOfTeam(t.id);
    const formDiv = odForm.divisionId === NO_DIVISION ? null : odForm.divisionId;
    const dirty = odForm.name.trim() !== t.name
      || formDiv !== (t.divisionId ?? null)
      || Math.trunc(Number(odForm.sortOrder) || 0) !== (t.sortOrder ?? 0);
    // 팀장은 **그 팀 사람 중에서만** 세운다 — 서버가 팀원 행의 isLeader 를 켜기 때문이다.
    const candidates = list.filter((u) => u.id !== lead?.user.id);

    return (
      <div className="um-od">
        <div className="um-od__h">
          <span className="um-mk um-mk--team" style={hueStyle(teamHue(t))} aria-hidden="true"><i /></span>
          <h3>{t.name}</h3>
          <span className="um-od__kind">팀</span>
          {t.divisionName && <span className="um-od__crumb">{t.divisionName}</span>}
          {!t.isActive && <span className="um-node__off">비활성</span>}
          <span className="um-od__sp" />
          <button type="button" className="um-btn um-btn--sm" disabled={odBusy}
            onClick={() => void toggleActive('team', t.id, !t.isActive, t.name)}>{t.isActive ? '비활성' : '활성'}</button>
          <button type="button" className="um-btn um-btn--sm um-btn--danger" disabled={odBusy}
            onClick={() => void removeOrg('team', t.id, t.name)}>삭제</button>
        </div>

        <div className="um-od__form">
          <div className="um-f">
            <label htmlFor="um-od-name">팀 이름</label>
            <input id="um-od-name" className="um-inp" maxLength={100} value={odForm.name}
              onChange={(e) => setOdForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="um-f">
            <label htmlFor="um-od-div">소속 부서</label>
            {/* 스크롤 컨테이너 안이라 portal 필수 — 없으면 드롭다운이 잘린다. */}
            <StyledSelect portal value={odForm.divisionId} options={divisionOptions}
              onChange={(v) => setOdForm((f) => ({ ...f, divisionId: v }))} />
          </div>
          <div className="um-f">
            <label htmlFor="um-od-sort">정렬 순서</label>
            <input id="um-od-sort" className="um-inp" type="number" min={0} max={SORT_MAX} value={odForm.sortOrder}
              onChange={(e) => setOdForm((f) => ({ ...f, sortOrder: e.target.value }))} />
          </div>
        </div>
        {odSaveRow(dirty, '부서를 비우면 회사 바로 아래에 놓입니다 — 팀의 부서는 선택사항입니다.')}

        {/* ── 팀장 ── */}
        <section className="um-od__card">
          <div className="um-od__ct">
            <UserCheck size={14} aria-hidden />팀장<span className="sub">결재 사다리의 첫 칸</span>
          </div>
          {lead ? (
            <div className="um-od__lead">
              <span className="um-av um-av--lg" style={hueStyle(teamHue(t))} aria-hidden="true">
                {initial(lead.user.name, lead.user.email)}
              </span>
              <span className="um-od__leadid">
                <b>{lead.user.name || '(이름 없음)'}</b>
                {lead.user.position && <em>{lead.user.position}</em>}
                {/* ★ 서버에 「팀장이 된 날」은 없다 — 있는 것은 «이 팀에 들어온 날» 이다. 그대로 적는다. */}
                <span className="um-od__sub">
                  {lead.joinedAt ? `${lead.joinedAt.slice(0, 10)} 이 팀에 합류` : '합류일 기록 없음'}
                </span>
              </span>
              <button type="button" className="um-btn um-btn--sm" disabled={odBusy}
                onClick={() => void toggleLeader(lead.user.id, t.id, false)}>해제</button>
            </div>
          ) : (
            <p className="um-od__none">
              팀장이 없습니다 — 이 팀의 휴가·연장 결재는 <b>부서장</b>(없으면 대표)으로 올라갑니다.
            </p>
          )}

          {candidates.length > 0 ? (
            <div className="um-od__pick">
              <span className="um-selw">
                <select className="um-inp" style={{ height: 32, width: 200, fontSize: '0.8rem' }}
                  aria-label="팀장으로 지정할 사람" value={leadPick} onChange={(e) => setLeadPick(e.target.value)}>
                  <option value="">{lead ? '팀장 바꾸기…' : '팀장 지정…'}</option>
                  {candidates.map((u) => (
                    <option key={u.id} value={u.id}>{u.position ? `${u.name} · ${u.position}` : u.name}</option>
                  ))}
                </select>
                <ChevronRight size={12} style={{ transform: 'rotate(90deg)' }} />
              </span>
              <button type="button" className="um-btn um-btn--sm" disabled={!leadPick || odBusy}
                onClick={() => { const id = leadPick; setLeadPick(''); void toggleLeader(id, t.id, true); }}>
                {lead ? '바꾸기' : '지정'}
              </button>
              {lead && <span className="um-f__hint">바꾸면 지금 팀장은 서버가 함께 내립니다 — 한 팀에 장은 하나입니다.</span>}
            </div>
          ) : (
            <p className="um-f__hint">
              {list.length === 0 ? '팀원이 없어 팀장을 세울 수 없습니다 — 먼저 팀원을 넣으세요.' : '이 팀에는 다른 팀원이 없습니다.'}
            </p>
          )}

          <p className="um-od__note">
            팀장을 지정하면 그 사람에게 <b>「우리 조직」 메뉴</b>가 뜨고 <b>휴가·연장 결재</b>가 옵니다.
          </p>
        </section>

        {/* ── 팀원 ── */}
        <section className="um-od__card">
          <div className="um-od__ct">
            <Users size={14} aria-hidden />팀원<span className="sub">{list.length}명</span>
            <span className="um-od__sp" />
            <button type="button" className="um-btn um-btn--sm" disabled={bulkBusy}
              onClick={() => setAddMem({ teamId: t.id, q: '' })}><UserPlus size={13} /> 팀원 추가</button>
          </div>
          {list.length === 0 ? (
            <p className="um-od__none">아직 팀원이 없습니다. 「팀원 추가」로 넣거나, 트리에 사람을 끌어다 놓으세요.</p>
          ) : (
            <div className="um-od__chips">{list.map((u) => odChip(u, t.id))}</div>
          )}
          <p className="um-od__note">
            사람을 누르면 그 사람의 정보·소속이 열립니다. <b>발령 이력이 남는 인사 이동</b>은
            인사 › 직원 관리 · 인사발령이 합니다 — 여기서는 조직의 뼈대만 다룹니다.
          </p>
        </section>

        <div className="um-od__warn">
          <AlertTriangle size={14} aria-hidden />
          <span>
            <b>지우면 사람이 갈 곳이 없어집니다.</b> 팀원·게시판·파일이 남아 있는 팀은 지우기 전에
            <b> 어디로 옮길지</b>를 먼저 묻습니다.
          </span>
        </div>
      </div>
    );
  }

  function renderDivPane(d: OrgDivision) {
    const kids = teamsOfDiv(d.id);
    const list = membersOfDiv(d.id);
    const leaderId = divLeaderOf.get(d.id) ?? null;
    const leader = leaderId ? usersById.get(leaderId) ?? null : null;
    const dirty = odForm.name.trim() !== d.name
      || Math.trunc(Number(odForm.sortOrder) || 0) !== (d.sortOrder ?? 0);
    const candidates = list.filter((u) => u.id !== leaderId);

    return (
      <div className="um-od">
        <div className="um-od__h">
          <span className="um-mk um-mk--dept" style={hueStyle(hueOf(d.id))} aria-hidden="true"><i /></span>
          <h3>{d.name}</h3>
          <span className="um-od__kind">부서</span>
          {!d.isActive && <span className="um-node__off">비활성</span>}
          <span className="um-od__sp" />
          <button type="button" className="um-btn um-btn--sm" disabled={odBusy}
            onClick={() => { setOpenDivs((p) => ({ ...p, [d.id]: true })); setMk({ kind: 'team', name: '', divisionId: d.id }); }}>
            <Plus size={13} /> 팀 추가
          </button>
          <button type="button" className="um-btn um-btn--sm" disabled={odBusy}
            onClick={() => void toggleActive('div', d.id, !d.isActive, d.name)}>{d.isActive ? '비활성' : '활성'}</button>
          <button type="button" className="um-btn um-btn--sm um-btn--danger" disabled={odBusy}
            onClick={() => void removeOrg('div', d.id, d.name)}>삭제</button>
        </div>

        <div className="um-od__form um-od__form--2">
          <div className="um-f">
            <label htmlFor="um-od-name">부서 이름</label>
            <input id="um-od-name" className="um-inp" maxLength={100} value={odForm.name}
              onChange={(e) => setOdForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="um-f">
            <label htmlFor="um-od-sort">정렬 순서</label>
            <input id="um-od-sort" className="um-inp" type="number" min={0} max={SORT_MAX} value={odForm.sortOrder}
              onChange={(e) => setOdForm((f) => ({ ...f, sortOrder: e.target.value }))} />
          </div>
        </div>
        {odSaveRow(dirty, '정렬 순서가 작을수록 트리에서 위에 섭니다.')}

        {/* ── 부서장 ──
            ★ 팀장과 달리 **칸 하나**(`leader_user_id`)라 새로 쓰면 옛 사람이 저절로 밀린다.
              근태 › 대표·부서장과 **같은 라우트**를 부르므로 두 화면이 갈리지 않는다. */}
        <section className="um-od__card">
          <div className="um-od__ct">
            <UserCheck size={14} aria-hidden />부서장<span className="sub">팀장 다음 칸</span>
          </div>
          {leader ? (
            <div className="um-od__lead">
              <span className="um-av um-av--lg" style={hueStyle(hueOf(d.id))} aria-hidden="true">
                {initial(leader.name, leader.email)}
              </span>
              <span className="um-od__leadid">
                <b>{leader.name || '(이름 없음)'}</b>
                {leader.position && <em>{leader.position}</em>}
                <span className="um-od__sub">이 부서의 결재를 받습니다</span>
              </span>
              <button type="button" className="um-btn um-btn--sm" disabled={odBusy}
                onClick={() => void saveDivLeader(d.id, null)}>해제</button>
            </div>
          ) : (
            <p className="um-od__none">
              부서장이 없습니다 — 팀장이 없는 팀의 결재가 <b>대표</b>까지 곧장 올라갑니다.
            </p>
          )}
          {candidates.length > 0 ? (
            <div className="um-od__pick">
              <span className="um-selw">
                <select className="um-inp" style={{ height: 32, width: 200, fontSize: '0.8rem' }}
                  aria-label="부서장으로 지정할 사람" value={leadPick} onChange={(e) => setLeadPick(e.target.value)}>
                  <option value="">{leader ? '부서장 바꾸기…' : '부서장 지정…'}</option>
                  {candidates.map((u) => (
                    <option key={u.id} value={u.id}>{u.position ? `${u.name} · ${u.position}` : u.name}</option>
                  ))}
                </select>
                <ChevronRight size={12} style={{ transform: 'rotate(90deg)' }} />
              </span>
              <button type="button" className="um-btn um-btn--sm" disabled={!leadPick || odBusy}
                onClick={() => void saveDivLeader(d.id, leadPick)}>{leader ? '바꾸기' : '지정'}</button>
            </div>
          ) : (
            <p className="um-f__hint">
              {leader
                ? '바꾸려면 이 부서의 하위 팀에 다른 사람이 있어야 합니다 — 부서장은 그 부서 인원 중에서 세웁니다.'
                : (list.length === 0
                  ? '이 부서의 하위 팀에 사람이 없습니다 — 부서장은 그 부서 인원 중에서 세웁니다.'
                  : '이 부서에는 다른 인원이 없습니다.')}
            </p>
          )}
        </section>

        {/* ── 하위 팀 ── */}
        <section className="um-od__card">
          <div className="um-od__ct">
            <Users size={14} aria-hidden />하위 팀<span className="sub">{kids.length}개 · {countOfDiv(d.id)}명</span>
          </div>
          {kids.length === 0 ? (
            <p className="um-od__none">아직 팀이 없습니다. 「팀 추가」로 만드세요 — 사람은 팀에 소속됩니다.</p>
          ) : (
            <div className="um-od__chips">
              {kids.map((t) => (
                <button key={t.id} type="button" className={`um-chip um-chip--org${t.isActive ? '' : ' is-off'}`}
                  style={hueStyle(teamHue(t))} onClick={() => setSel({ kind: 'team', id: t.id })}>
                  <span className="um-mk um-mk--team" aria-hidden="true"><i /></span>
                  <b>{t.name}</b>
                  <em>{countByTeam.get(t.id) ?? 0}명</em>
                  {leaderEntryOfTeam(t.id)
                    ? <span className="um-tag um-tag--lead">팀장 있음</span>
                    : <span className="um-chip__off">팀장 없음</span>}
                </button>
              ))}
            </div>
          )}
        </section>

        <div className="um-od__warn">
          <AlertTriangle size={14} aria-hidden />
          <span>
            부서를 지우면 <b>하위 팀도 함께</b> 사라집니다. 게시판·파일이 남아 있으면
            지우기 전에 <b>어디로 옮길지</b>를 먼저 묻습니다.
          </span>
        </div>
      </div>
    );
  }

  /** 전체(회사) · 미배정 — 조직 하나를 고르기 전에 보는 화면. */
  function renderScopePane() {
    const isNone = sel.kind === 'none';
    const rep = repId ? usersById.get(repId) ?? null : null;
    return (
      <div className="um-od">
        <div className="um-od__h">
          <span className={`um-mk ${isNone ? 'um-mk--plain' : 'um-mk--company'}`} aria-hidden="true"><i /></span>
          <h3>{isNone ? `${company || '회사'} 직속` : (company || '전체')}</h3>
          {/* ★ 「어느 팀에도 없는 사람」은 «없다»로 읽힌다 — 있는 자리를 말한다. */}
          <span className="um-od__kind">{isNone ? '회사에 바로 속한 사람' : '회사'}</span>
          <span className="um-od__sp" />
        </div>

        {isNone ? (
          <>
            {unassigned.length === 0 ? (
              <p className="um-od__none">회사에 바로 속한 사람이 없습니다 — 모두 팀에 있습니다.</p>
            ) : (
              <section className="um-od__card">
                <div className="um-od__ct">
                  <Users size={14} aria-hidden />회사 직속<span className="sub">{unassigned.length}명</span>
                  <span className="um-od__sp" />
                  {pickedIds.length > 0 && <span className="um-f__hint">{pickedIds.length}명 선택</span>}
                </div>
                <div className="um-od__chips">
                  {unassigned.map((u) => (
                    <span key={u.id} className={`um-chip${picked[u.id] ? ' is-sel' : ''}`}>
                      <label className="um-chk" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={!!picked[u.id]} aria-label={`${u.name} 선택`}
                          onChange={() => setPicked((pk) => {
                            const next = { ...pk };
                            if (next[u.id]) delete next[u.id]; else next[u.id] = true;
                            return next;
                          })} />
                      </label>
                      <button type="button" className="um-chip__go" onClick={() => setDrawer({ mode: 'edit', userId: u.id })}>
                        <span className="um-av" aria-hidden="true">{initial(u.name, u.email)}</span>
                        <b>{u.name || '(이름 없음)'}</b>
                        {u.position && <em>{u.position}</em>}
                      </button>
                    </span>
                  ))}
                </div>
                <div className="um-od__pick">
                  <span className="um-selw">
                    <select className="um-inp" style={{ height: 32, width: 220, fontSize: '0.8rem' }}
                      aria-label="배정할 팀" value={bulkTeam} onChange={(e) => setBulkTeam(e.target.value)}>
                      <option value="">팀 선택…</option>
                      {teams.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.divisionName ? `${t.divisionName} · ${t.name}` : `${t.name} (부서 없음)`}
                        </option>
                      ))}
                    </select>
                    <ChevronRight size={12} style={{ transform: 'rotate(90deg)' }} />
                  </span>
                  <button type="button" className="um-btn um-btn--sm um-btn--primary"
                    disabled={!bulkTeam || pickedIds.length === 0 || bulkBusy}
                    onClick={() => void moveUsersToTeam(pickedIds, bulkTeam)}>
                    {bulkBusy ? '옮기는 중…' : (pickedIds.length ? `${pickedIds.length}명 배정` : '배정')}
                  </button>
                  {/* ★ 「필요합니다」가 아니라 «옮길 수 있습니다» 다 — 도구는 그대로, 재촉만 뺐다. */}
                  <span className="um-f__hint">팀으로 옮길 사람만 고르면 됩니다 — 회사 직속으로 두어도 됩니다.</span>
                </div>
              </section>
            )}
          </>
        ) : (
          <>
            <dl className="um-od__sum">
              <div><dt>부서</dt><dd>{divs.length}</dd></div>
              <div><dt>팀</dt><dd>{teams.length}<span>부서 없음 {rootTeams.length}</span></dd></div>
              <div><dt>인원</dt><dd>{users.length}<span>활성 {users.filter(isLive).length}</span></dd></div>
              {/* ★ 경고색을 뺐다 — 회사 직속은 «고쳐야 할 수»가 아니라 그냥 수다. */}
              <div><dt>회사 직속</dt><dd>{unassigned.length}</dd></div>
            </dl>

            <section className="um-od__card">
              <div className="um-od__ct"><UserCheck size={14} aria-hidden />대표<span className="sub">사다리의 마지막 칸</span></div>
              {rep ? (
                <div className="um-od__lead">
                  <span className="um-av um-av--lg" aria-hidden="true">{initial(rep.name, rep.email)}</span>
                  <span className="um-od__leadid">
                    <b>{rep.name || '(이름 없음)'}</b>
                    {rep.position && <em>{rep.position}</em>}
                    <span className="um-od__sub">팀장·부서장이 없으면 여기로 옵니다</span>
                  </span>
                </div>
              ) : (
                <p className="um-od__none">
                  대표가 지정돼 있지 않습니다 — 팀장·부서장이 모두 없는 팀은 <b>결재선을 못 만듭니다</b>.
                </p>
              )}
              {/* ★ 대표는 회사 한 칸이라 조직도가 아니라 **회사 쪽**에서 정한다 — 어디인지 적어 둔다. */}
              <p className="um-od__note">대표는 <b>인사 › 조직 · 권한 › 대표 · 부서장</b>에서 지정합니다.</p>
            </section>

            <p className="um-od__none">왼쪽 트리에서 부서나 팀을 고르면 여기서 이름·소속·팀장·팀원을 다룹니다.</p>
          </>
        )}

        {!isNone && (
          <div className="um-od__warn um-od__warn--info">
            <AlertTriangle size={14} aria-hidden />
            <span><b>부서 없이 팀만</b> 두어도 됩니다 — 팀의 부서는 선택사항입니다.</span>
          </div>
        )}
      </div>
    );
  }

  function renderOrgPane() {
    if (loading) {
      return (
        <div className="um-od">
          {Array.from({ length: 4 }, (_, i) => (
            <span key={i} className="um-skel" style={{ height: i === 0 ? 40 : 92, borderRadius: 12, display: 'block' }} />
          ))}
        </div>
      );
    }
    if (sel.kind === 'team') {
      const t = teamById.get(sel.id);
      return t ? renderTeamPane(t) : renderScopePane();
    }
    if (sel.kind === 'div') {
      const d = divById.get(sel.id);
      return d ? renderDivPane(d) : renderScopePane();
    }
    return renderScopePane();
  }

  /* ── 경로 ── */
  function renderCrumb() {
    if (sel.kind === 'all') return <b>전체 사용자</b>;
    if (sel.kind === 'none') return <b>회사 직속</b>;
    if (sel.kind === 'div') return <b>{divById.get(sel.id)?.name ?? '부서'}</b>;
    const t = teamById.get(sel.id);
    return (
      <>
        {t?.divisionName
          ? <>{t.divisionName} <span className="sep">/</span> </>
          : <span className="sep">부서 없음 / </span>}
        <b>{t?.name ?? '팀'}</b>
      </>
    );
  }

  /* ── 드롭다운 메뉴 내용 ── */
  function renderMenu() {
    if (!menu) return null;
    const style: CSSProperties = { left: Math.max(8, menu.x - 190), top: menu.y + 4 };
    if (menu.kind === 'org') {
      const [k, id] = [menu.id.slice(0, 1), menu.id.slice(2)];
      if (k === 'd') {
        const d = divById.get(id);
        if (!d) return null;
        return (
          <div className="um-menu" style={style} role="menu">
            <div className="um-menu__l">{d.name}</div>
            <button type="button" role="menuitem" onClick={() => { setMenu(null); setRenaming({ kind: 'div', id }); setRenameName(d.name); }}>이름 변경</button>
            <button type="button" role="menuitem" onClick={() => { setMenu(null); setOpenDivs((p) => ({ ...p, [id]: true })); setMk({ kind: 'team', name: '', divisionId: id }); }}>이 부서에 팀 추가</button>
            <button type="button" role="menuitem" onClick={() => { setMenu(null); void toggleActive('div', id, !d.isActive, d.name); }}>{d.isActive ? '비활성으로 전환' : '활성으로 전환'}</button>
            <hr />
            <button type="button" role="menuitem" className="is-danger" onClick={() => { setMenu(null); void removeOrg('div', id, d.name); }}>삭제</button>
          </div>
        );
      }
      const t = teamById.get(id);
      if (!t) return null;
      return (
        <div className="um-menu" style={style} role="menu">
          <div className="um-menu__l">{t.name}</div>
          <button type="button" role="menuitem" onClick={() => { setMenu(null); setRenaming({ kind: 'team', id }); setRenameName(t.name); }}>이름 변경</button>
          <button type="button" role="menuitem" onClick={() => { setMenu(null); void toggleActive('team', id, !t.isActive, t.name); }}>{t.isActive ? '비활성으로 전환' : '활성으로 전환'}</button>
          <hr />
          <div className="um-menu__l">상위 부서</div>
          <button type="button" role="menuitem" disabled={!t.divisionId} onClick={() => { setMenu(null); void moveTeam(id, null); }}>부서에서 분리</button>
          {divs.map((d) => (
            <button key={d.id} type="button" role="menuitem" disabled={t.divisionId === d.id}
              onClick={() => { setMenu(null); void moveTeam(id, d.id); }}>{d.name} 으로 이동</button>
          ))}
          <hr />
          <button type="button" role="menuitem" className="is-danger" onClick={() => { setMenu(null); void removeOrg('team', id, t.name); }}>삭제</button>
        </div>
      );
    }
    const u = users.find((x) => x.id === menu.id);
    if (!u) return null;
    const myTeams = members[u.id] ?? [];
    return (
      <div className="um-menu" style={style} role="menu">
        <div className="um-menu__l">{u.name}</div>
        <button type="button" role="menuitem" onClick={() => { setMenu(null); setDrawer({ mode: 'edit', userId: u.id }); }}>인사정보 열기</button>
        {myTeams.map((m) => (
          <button key={m.teamId} type="button" role="menuitem"
            onClick={() => { setMenu(null); void toggleLeader(u.id, m.teamId, !m.isLeader); }}>
            {teamById.get(m.teamId)?.name ?? '팀'} 팀장 {m.isLeader ? '해제' : '지정'}
          </button>
        ))}
        <hr />
        <div className="um-menu__l">소속 이동</div>
        {teams.map((t) => (
          <button key={t.id} type="button" role="menuitem" disabled={myTeams.some((m) => m.teamId === t.id)}
            onClick={() => { setMenu(null); void moveUsersToTeam([u.id], t.id); }}>
            {t.divisionName ? `${t.divisionName} · ${t.name}` : `${t.name} (부서 없음)`}
          </button>
        ))}
        {myTeams.length > 0 && (
          <>
            <hr />
            <button type="button" role="menuitem" className="is-danger" onClick={() => { setMenu(null); void detachUsers([u.id]); }}>소속에서 제외</button>
          </>
        )}
      </div>
    );
  }

  /* ═══════════════ 렌더 ═══════════════ */
  const visibleDivs = sortedDivs.filter((d) => matchQ(d.name) || teamsOfDiv(d.id).some((t) => matchQ(t.name)));
  const visibleRootTeams = rootTeams.filter((t) => matchQ(t.name));

  return (
    <>
    {/* ── 머리 — 화면 이름과 «지금 회사가 어떤가» (2026-08-31 · 시안 6/9) ───────────
        ★ 조직도는 통합설정 안의 한 칸이라 제 제목이 없었다. 시안이 머리를 그린 이유는
          이름 때문이 아니라 **숫자 넷** 때문이다 — 미배정이 몇 명인지가 여기 서야 보인다.
        ★ 「생성」 단추 하나를 **부서 추가 · 팀 추가 둘로** 갈랐다. 종전에는 눌러야 종류를
          고를 수 있어, 무엇을 만들 수 있는지가 화면에 안 적혀 있었다. */}
    {isOrgMode && (
      <header className="um-orgh">
        <div className="um-orgh__t">
          <h2>조직도</h2>
          <p>
            {/* ⚠ 「활성」은 **`isActive`** 로 센다 — `status` 는 계정 상태가 아니라 **접속 상태**다
                (2026-08-31 운영 실측: 11명 전원 `offline` 이라 이 숫자가 늘 0 이었다). */}
            부서 {divs.length} · 팀 {teams.length} · 활성 {users.filter(isLive).length}명
            {' · '}
            <span>회사 직속 {unassigned.length}명</span>
          </p>
        </div>
        <span className="um-orgh__sp" />
        <button type="button" className="um-btn um-btn--sm" onClick={() => openMk('div')}>
          <Plus size={14} /> 부서 추가
        </button>
        <button type="button" className="um-btn um-btn--sm um-btn--primary" onClick={() => openMk('team')}>
          <Plus size={14} /> 팀 추가
        </button>
      </header>
    )}

    {/* ★★★ **자식이 하나면 두 칸 격자에 갇힌다** (2026-08-31 운영 실측 — #790 과 같은 모양).
        `.um-ws` 는 «트리 + 목록» 2열(296px + 1fr)인데, 판 4 에서 계정 화면의 왼쪽 트리를
        걷어내며 **격자는 그대로 두었다.** 그래서 사용자 관리가 첫 칸 **296px** 에 갇혀
        이름·이메일·전화번호가 세로로 접히고, 오른쪽 858px 이 통째로 비어 있었다. */}
    <div className={`um-ws${isOrgMode ? '' : ' um-ws--solo'}`} onClick={() => menu && setMenu(null)}>

      {/* ── 좌: 부서 · 팀 ──
          ★ 계정 화면에는 없다(판 4) — 여기서 소속을 못 고치니 트리가 «걸러 보기» 뿐이고,
            그건 오른쪽 검색으로 된다. 조직도는 통합설정 › 조직 › 조직도가 갖는다. */}
      {isOrgMode && (
      <aside className="um-panel um-panel--org">
        {/* ★ 제목이 「조직도」에서 「부서 · 팀」으로 바뀌었다 — 화면 이름은 위 머리가 갖는다.
            같은 이름을 두 번 세우면 어느 쪽이 이 칸의 이름인지 흐려진다. */}
        <div className="um-ph">
          <Building2 size={15} />
          <h3>부서 · 팀</h3>
          <span className="um-ph__n">{divs.length}부서 · {teams.length}팀</span>
          <span className="um-ph__sp" />
        </div>

        <div className="um-org__tools">
          <label className="um-search">
            <Search size={14} />
            <input value={orgQ} placeholder="부서 · 팀 검색" aria-label="부서 팀 검색" onChange={(e) => setOrgQ(e.target.value)} />
          </label>
        </div>

        <nav className="um-tree" aria-label="조직도" role="tree">
          {/* 루트 = 회사. 부서와 무소속 팀은 그 아래 형제로 놓인다(스키마 용어를 노출하지 않는다). */}
          <div className={`um-node um-node--root${isSel('all') ? ' is-sel' : ''}`}
            role="treeitem" aria-selected={isSel('all')} tabIndex={0}
            onClick={() => setSel({ kind: 'all' })}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSel({ kind: 'all' }); } }}>
            <span className="um-tw is-leaf" />
            <span className="um-mk um-mk--company"><i /></span>
            <span className="um-node__nm">{company || '전체 사용자'}</span>
            <span className="um-node__n">{users.length}</span>
          </div>

          <div className="um-kids">
            {visibleDivs.map(renderDivNode)}
            {visibleRootTeams.map(renderTeamNode)}
            {/* 검색 중에는 조직만 남긴다 — 직속은 부서·팀 이름으로 찾는 대상이 아니다. */}
            {orgQ.trim() === '' && (
              <div className={`um-node${isSel('none') ? ' is-sel' : ''}`}
                role="treeitem" aria-selected={isSel('none')} tabIndex={0}
                onClick={() => setSel({ kind: 'none' })}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSel({ kind: 'none' }); } }}>
                <span className="um-tw is-leaf" />
                <span className="um-mk um-mk--plain"><i /></span>
                {/**
                  * ★★★ **「미배정」이 아니라 「회사 직속」이다** (2026-08-31 · 사용자 결정).
                  *    「조직도에서 미배정은 **회사 아래로 포함된다**. 따라서 김철희 대표는
                  *     (주)타임소프트 아래로 배정된다. 또한 AI-bot, admin 마찬가지이다.」
                  *
                  *    **대표는 팀에 속하지 않는 것이 정상**이고(결재 사다리의 마지막 칸이다),
                  *    시스템 계정도 그렇다. 그런데 화면은 이 셋을 「미배정 — 배정이 필요합니다」로
                  *    세워, **고쳐야 할 잘못처럼** 보이게 했다. 팀이 없다는 것은 «어디에도 없다»가
                  *    아니라 **«회사에 바로 속한다»** 는 뜻이다.
                  */}
                <span className="um-node__nm">회사 직속</span>
                <span className="um-node__n">{unassigned.length}</span>
              </div>
            )}
          </div>

          {orgQ.trim() !== '' && visibleDivs.length === 0 && visibleRootTeams.length === 0 && (
            <p className="um-f__hint" style={{ padding: '10px 9px' }}>검색 결과가 없습니다.</p>
          )}
          {/**
            * ★★★ **재촉하던 줄을 걷어냈다** (2026-08-31 · 사용자 결정).
            *    종전: 「어느 팀에도 없는 인원 N명 — **워크스페이스 용량이 팀 기준이라 배정이
            *    필요합니다**」(경고색). 두 가지가 틀렸다:
            *      ① 회사 직속은 **잘못이 아니다** — 대표·시스템 계정의 제자리다.
            *      ② **그 이유가 사실이 아니다.** 용량 범위는 개인·팀·회사 셋이고
            *         (`workspace-quota.ts`), **개인 한도(5GiB)의 키는 `userId`** 라 팀과 무관하다.
            *         팀이 없어도 개인 용량은 그대로다.
            *    ★ 팀으로 옮기는 도구는 **그대로 둔다** — 옮길 사람은 여전히 있다(재촉만 뺐다).
            */}

          <div className={`um-drop${dropTarget === 'root' ? ' is-drop' : ''}`}
            onDragOver={(e) => { if (canDrop('root')) { e.preventDefault(); setDropTarget('root'); } }}
            onDragLeave={() => setDropTarget((p) => (p === 'root' ? null : p))}
            onDrop={(e) => onDropNode(e, { kind: 'root' })}>
            팀을 여기로 끌면 부서에서 빠져 회사 바로 아래로 옮겨집니다
          </div>
        </nav>

        {/* ★ 트리 아래 있던 «작은 요약 카드» 는 걷어냈다 (2026-08-31 · 시안 6/9) —
            같은 것을 오른쪽이 훨씬 크게 말한다. 카드가 하던 일(이름 변경 · 팀 추가 ·
            활성 · 부서에서 분리 · 삭제)은 하나도 안 빠지고 상세로 옮겨 갔다. */}
      </aside>
      )}

      {/* ── 우: 조직도면 «고른 조직의 상세», 계정 화면이면 종전 사용자 표 ── */}
      <section className={isOrgMode ? 'um-panel um-panel--od' : 'um-panel um-panel--user'}>
        {isOrgMode ? renderOrgPane() : (
        <>
        <div className="um-ph">
          <span className="um-crumb">
            {renderCrumb()} <span className="cnt">{scoped.length}명</span>
          </span>
          {/* ★ 「메뉴 권한」 탭은 뺐다 (2026-08-28 · 판 4) — 제 화면을 가졌다:
              통합설정 › 조직 › 메뉴 권한. 거기서는 «부서·팀 없는 직원» 도 대상이 된다. */}
          <span className="um-ph__sp" />
          {!isOrgMode && (
            <button type="button" className="um-btn um-btn--primary um-btn--sm" onClick={() => setDrawer({ mode: 'new' })}>
              <UserPlus size={13} /> 사용자 등록
            </button>
          )}
        </div>

        <>
        <div className="um-bar">
          <div className="um-seg" role="group" aria-label="계정 상태 필터">
            {STATUS_SEG.map((s) => {
              const n = s.key === 'all' ? scoped.length : scoped.filter((u) => isLive(u) === (s.key === 'on')).length;
              return (
                <button key={s.key} type="button" className={status === s.key ? 'is-on' : ''}
                  aria-pressed={status === s.key} onClick={() => setStatus(s.key)}>
                  {s.label}<span className="n">{n}</span>
                </button>
              );
            })}
          </div>
          <label className="um-search">
            <Search size={14} />
            <input value={q} placeholder="이름 · 사번 · 이메일" aria-label="사용자 검색" onChange={(e) => setQ(e.target.value)} />
          </label>
          <span className="um-selw">
            <select className="um-inp" style={{ width: 118 }} aria-label="권한 필터" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="">권한 전체</option>
              <option value="ADMIN">관리자</option>
              <option value="MANAGER">매니저</option>
              <option value="STAFF">직원</option>
            </select>
            <ChevronRight size={12} style={{ transform: 'rotate(90deg)' }} />
          </span>
        </div>

        {err && <div className="um-alert" role="alert"><AlertTriangle size={15} /><span>{err}</span></div>}
        {notice && (
          <div className={`um-alert ${notice.tone === 'warn' ? 'um-alert--warn' : 'um-alert--ok'}`} role="status">
            <Check size={15} /><span>{notice.text}</span>
          </div>
        )}

        {pickedIds.length > 0 && (
          <div className="um-bulk">
            <span className="um-bulk__n">{pickedIds.length}명 선택</span>
            <span className="um-selw">
              <select className="um-inp um-btn--sm" style={{ height: 29, width: 190, fontSize: '0.78rem' }}
                aria-label="이동할 팀" value={bulkTeam} onChange={(e) => setBulkTeam(e.target.value)}>
                <option value="">팀 선택…</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.divisionName ? `${t.divisionName} · ${t.name}` : `${t.name} (부서 없음)`}</option>
                ))}
              </select>
              <ChevronRight size={12} style={{ transform: 'rotate(90deg)' }} />
            </span>
            <button type="button" className="um-btn um-btn--sm um-btn--primary" disabled={!bulkTeam || bulkBusy}
              onClick={() => void moveUsersToTeam(pickedIds, bulkTeam)}>소속 이동</button>
            <span className="um-selw">
              <select className="um-inp um-btn--sm" style={{ height: 29, width: 132, fontSize: '0.78rem' }}
                aria-label="변경할 권한" value={bulkRole} onChange={(e) => setBulkRole(e.target.value as AssignableRole)}>
                {myRoleOptions.map((r) => <option key={r} value={r}>{ROLE_OPTION_LABEL[r]}</option>)}
              </select>
              <ChevronRight size={12} style={{ transform: 'rotate(90deg)' }} />
            </span>
            <button type="button" className="um-btn um-btn--sm" disabled={bulkBusy} onClick={() => void applyBulkRole()}>권한 변경</button>
            <button type="button" className="um-btn um-btn--sm um-btn--danger" disabled={bulkBusy} onClick={() => void detachUsers(pickedIds)}>소속 제외</button>
            <span className="um-bulk__sp" />
            <button type="button" className="um-btn um-btn--ghost um-btn--sm" onClick={() => setPicked({})}>선택 해제</button>
          </div>
        )}

        <div className="um-lh">
          <label className="um-chk">
            <input type="checkbox" aria-label="이 페이지 전체 선택"
              checked={rows.length > 0 && rows.every((u) => picked[u.id])}
              ref={(el) => { if (el) el.indeterminate = rows.some((u) => picked[u.id]) && !rows.every((u) => picked[u.id]); }}
              onChange={(e) => {
                setPicked((p) => {
                  const next = { ...p };
                  rows.forEach((u) => { if (e.target.checked) next[u.id] = true; else delete next[u.id]; });
                  return next;
                });
              }} />
          </label>
          <span><b>{filtered.length}</b>명</span>
          <span className="um-lh__sp" />
          <span style={{ fontSize: '0.72rem' }}>정렬</span>
          <span className="um-selw">
            <select className="um-inp" style={{ height: 29, width: 138, fontSize: '0.77rem' }} aria-label="정렬 기준"
              value={sort} onChange={(e) => setSort(e.target.value)}>
              {SORT_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <ChevronRight size={12} style={{ transform: 'rotate(90deg)' }} />
          </span>
        </div>

        <div className="um-list">
          {loading ? (
            Array.from({ length: 8 }, (_, i) => (
              <div className="um-row" key={i} aria-hidden="true">
                <span /><span className="um-skel um-skel--av" />
                <span style={{ minWidth: 0 }}>
                  <span className="um-skel" style={{ width: 120, display: 'block' }} />
                  <span className="um-skel" style={{ width: '70%', marginTop: 9, display: 'block' }} />
                </span>
                <span className="um-skel" style={{ width: 52, height: 20, borderRadius: 999 }} /><span />
              </div>
            ))
          ) : rows.length === 0 ? (
            <div className="um-blank">
              <Users size={30} style={{ color: 'var(--text-subtle)' }} />
              <b>{scoped.length === 0 ? '이 조직에는 아직 인원이 없습니다' : '조건에 맞는 사용자가 없습니다'}</b>
              <p>
                {scoped.length === 0
                  ? '오른쪽 목록의 사용자 행을 왼쪽 트리의 팀으로 끌어다 놓으면 소속이 바뀝니다.'
                  : '필터를 넓히거나 검색어를 지워 보세요.'}
              </p>
              {scoped.length > 0 && (
                <button type="button" className="um-btn um-btn--sm"
                  onClick={() => { setStatus('all'); setRoleFilter(''); setQ(''); }}>필터 초기화</button>
              )}
            </div>
          ) : rows.map((u) => {
            const org = orgLabelOf(u);
            const off = !isLive(u);
            return (
              <div key={u.id} className={`um-row${picked[u.id] ? ' is-sel' : ''}${off ? ' is-off' : ''}`}
                role="row" tabIndex={0} draggable
                onDragStart={() => { dragRef.current = { type: 'user', ids: picked[u.id] ? pickedIds : [u.id] }; }}
                onDragEnd={() => { dragRef.current = null; setDropTarget(null); }}
                onClick={() => setDrawer({ mode: 'edit', userId: u.id })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setDrawer({ mode: 'edit', userId: u.id }); } }}>
                <label className="um-chk" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={!!picked[u.id]} aria-label={`${u.name} 선택`}
                    onChange={() => setPicked((p) => {
                      const next = { ...p };
                      if (next[u.id]) delete next[u.id]; else next[u.id] = true;
                      return next;
                    })} />
                </label>
                <span className="um-av" style={hueStyle(org.hue)} aria-hidden="true">{initial(u.name, u.email)}</span>
                <span style={{ minWidth: 0 }}>
                  <span className="um-id">
                    <b>{u.name || '(이름 없음)'}</b>
                    {u.employeeNumber && <span className="no">{u.employeeNumber}</span>}
                    {u.role !== 'STAFF' && <span className={`um-tag um-tag--${u.role.toLowerCase()}`}>{ROLE_LABEL[u.role] ?? u.role}</span>}
                    {/* 대표·부서장은 역할이 아니라 지정 — 근태 › 대표·부서장에서 정한다. */}
                    {repId === u.id && <span className="um-tag um-tag--lead">대표</span>}
                    {divLeaders.has(u.id) && <span className="um-tag um-tag--lead">부서장</span>}
                    {isLeaderOf(u) && <span className="um-tag um-tag--lead">팀장</span>}
                  </span>
                  <span className="um-meta">
                    <span className="org" style={hueStyle(org.hue)}>
                      <i />{org.text}{org.extra > 0 ? ` 외 ${org.extra}` : ''}
                    </span>
                    {u.position && <span>{u.position}</span>}
                    <span>{u.email}</span>
                    {u.phone && <span className="mono">{u.phone}</span>}
                  </span>
                </span>
                <span className={`um-st um-st--${isLive(u) ? 'active' : 'disabled'}`}>{isLive(u) ? '활성' : '비활성'}</span>
                <button type="button" className="um-more" aria-label={`${u.name} 작업`}
                  onClick={(e) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setMenu({ x: r.right, y: r.bottom, kind: 'user', id: u.id }); }}>
                  <MoreVertical size={15} />
                </button>
              </div>
            );
          })}
        </div>

        {!loading && filtered.length > 0 && (
          <div className="um-foot">
            <span>
              전체 <b>{filtered.length}</b>명 중 <b>{(curPage - 1) * PAGE_SIZE + 1}–{Math.min(filtered.length, curPage * PAGE_SIZE)}</b>
            </span>
            <span className="um-pg">
              <button type="button" disabled={curPage === 1} aria-label="이전 페이지" onClick={() => setPage(curPage - 1)}>‹</button>
              {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
                <button key={n} type="button" className={n === curPage ? 'is-cur' : ''}
                  aria-current={n === curPage ? 'page' : undefined} onClick={() => setPage(n)}>{n}</button>
              ))}
              <button type="button" disabled={curPage === pageCount} aria-label="다음 페이지" onClick={() => setPage(curPage + 1)}>›</button>
            </span>
          </div>
        )}
        </>
        </>
        )}
      </section>

      {xfer && (() => {
        // 워크스페이스 공유·용량·파일은 팀만 가리킬 수 있는 컬럼이라, 남아 있으면 대상도 팀이어야 한다.
        const needsTeam = xfer.dep.workspaceShares + xfer.dep.workspaceQuotas + xfer.dep.files > 0;
        const skipTeams = xfer.kind === 'team' ? [xfer.id] : teamsOfDiv(xfer.id).map((t) => t.id);
        const opts: SelectOption[] = [
          ...teams.filter((t) => !skipTeams.includes(t.id)).map((t) => ({
            value: `team:${t.id}`,
            label: t.divisionName ? `${t.divisionName} · ${t.name}` : t.name,
          })),
          ...(needsTeam ? [] : sortedDivs
            .filter((d) => !(xfer.kind === 'div' && d.id === xfer.id))
            .map((d) => ({ value: `division:${d.id}`, label: `${d.name} (부서 전체)` }))),
        ];
        const rows = [
          { k: '게시판', v: xfer.dep.boards, sub: xfer.dep.boardNames.join(', ') },
          { k: '게시글', v: xfer.dep.posts, sub: '' },
          { k: '게시판 공유 설정', v: xfer.dep.boardShares, sub: '' },
          { k: '글 열람자 지정', v: xfer.dep.postReaders, sub: '' },
          { k: '워크스페이스 공유', v: xfer.dep.workspaceShares, sub: '' },
          { k: '워크스페이스 용량 설정', v: xfer.dep.workspaceQuotas, sub: '' },
          { k: '팀 소유 파일', v: xfer.dep.files, sub: '' },
        ].filter((r) => r.v > 0);

        return (
          <>
            <div className="um-scrim" onClick={() => setXfer(null)} />
            <div className="um-dlg" role="dialog" aria-modal="true" aria-label="연관 데이터 이관">
              <div className="um-dr__h">
                <span className="um-dr__t">
                  <b>먼저 이관이 필요합니다</b>
                  <span>"{xfer.name}" 에 연관된 데이터가 남아 있습니다</span>
                </span>
                <button type="button" className="um-dr__x" aria-label="닫기" onClick={() => setXfer(null)}><X size={15} /></button>
              </div>
              <div className="um-dr__b">
                <div className="um-alert um-alert--warn" style={{ margin: '0 0 14px' }}>
                  <AlertTriangle size={15} />
                  <span>그냥 지우면 아래 데이터가 없는 조직을 가리킨 채 남아 접근이 막힙니다.</span>
                </div>

                <div className="um-secl">남아 있는 것</div>
                <dl className="um-deplist">
                  {rows.map((r) => (
                    <div key={r.k}>
                      <dt>{r.k}</dt>
                      <dd>{r.v}건{r.sub ? <span> · {r.sub}</span> : null}</dd>
                    </div>
                  ))}
                </dl>

                <div className="um-secl">어디로 옮길까요</div>
                <div className="um-f">
                  <StyledSelect portal value={xfer.toId} options={opts} placeholder="이관할 조직 선택…"
                    onChange={(v) => setXfer({ ...xfer, toId: v })} />
                  <span className="um-f__hint">
                    {needsTeam
                      ? '워크스페이스 공유·용량·파일이 있어 팀으로만 옮길 수 있습니다.'
                      : '옮긴 뒤 이 조직을 삭제합니다. 이관이 실패하면 삭제하지 않습니다.'}
                  </span>
                </div>
              </div>
              <div className="um-dr__f">
                <button type="button" className="um-btn um-btn--danger" disabled={xfer.toId === '' || xfer.busy}
                  onClick={() => void runTransfer()}>{xfer.busy ? '처리 중…' : '이관 후 삭제'}</button>
                <button type="button" className="um-btn" onClick={() => setXfer(null)}>취소</button>
                <span className="sp" />
              </div>
            </div>
          </>
        );
      })()}

      {mk && (
        <>
          <div className="um-scrim" onClick={() => setMk(null)} />
          <div className="um-dlg" role="dialog" aria-modal="true" aria-label="조직 생성">
            <div className="um-dr__h">
              <span className="um-dr__t"><b>조직 생성</b><span>부서 또는 팀을 만듭니다</span></span>
              <button type="button" className="um-dr__x" aria-label="닫기" onClick={() => setMk(null)}><X size={15} /></button>
            </div>
            <div className="um-dr__b">
              <div className="um-secl">종류</div>
              <div className="um-kind" role="tablist" aria-label="조직 종류">
                <button type="button" role="tab" aria-selected={mk.kind === 'div'} onClick={() => setMk({ ...mk, kind: 'div' })}>
                  <span className="um-mk um-mk--dept" style={hueStyle('var(--primary)')}><i /></span>
                  <b>부서</b><span>팀을 묶는 상위 조직</span>
                </button>
                <button type="button" role="tab" aria-selected={mk.kind === 'team'} onClick={() => setMk({ ...mk, kind: 'team' })}>
                  <span className="um-mk um-mk--team" style={hueStyle('var(--primary)')}><i /></span>
                  <b>팀</b><span>사람이 소속되는 단위</span>
                </button>
              </div>

              <div className="um-secl">내용</div>
              <div className="um-fields">
                <div className="um-f w2">
                  <label htmlFor="um-mkname">{mk.kind === 'div' ? '부서명' : '팀명'}<em>*</em></label>
                  <input id="um-mkname" className="um-inp" autoFocus maxLength={100} value={mk.name}
                    placeholder={mk.kind === 'div' ? '예: 경영지원본부' : '예: 플랫폼팀'}
                    onChange={(e) => setMk({ ...mk, name: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') void submitMk(); if (e.key === 'Escape') setMk(null); }} />
                </div>
                {mk.kind === 'team' && (
                  <div className="um-f w2">
                    <label>상위 부서</label>
                    {/* 스크롤 컨테이너 안이라 portal 필수 — 없으면 드롭다운이 잘린다. */}
                    <StyledSelect portal value={mk.divisionId} options={divisionOptions}
                      onChange={(v) => setMk({ ...mk, divisionId: v })} />
                    <span className="um-f__hint">부서는 선택 사항입니다. 지정하지 않으면 회사 바로 아래에 놓입니다.</span>
                  </div>
                )}
              </div>
            </div>
            <div className="um-dr__f">
              <button type="button" className="um-btn um-btn--primary" disabled={mk.name.trim() === '' || orgBusy === 'mk'}
                onClick={() => void submitMk()}>{orgBusy === 'mk' ? '만드는 중…' : '생성'}</button>
              <button type="button" className="um-btn" onClick={() => setMk(null)}>취소</button>
              <span className="sp" />
            </div>
          </div>
        </>
      )}

      {/* ── 팀원 추가 (2026-08-31 · 시안 6/9) ─────────────────────────────────
          ★ 종전에는 오른쪽 계정 표에서 사람을 **끌어다** 트리에 놓는 길뿐이었다. 표가
            상세로 바뀌었으니 넣는 길을 여기 둔다 — 끌어다 놓기는 그대로 산다.
          ★ 부르는 것은 종전 `moveUsersToTeam` 이다. **넣기가 아니라 옮기기**이므로
            다른 팀에 있던 사람은 이 팀으로 «옮겨진다» — 화면이 그렇게 적는다. */}
      {addMem && (() => {
        const t = teamById.get(addMem.teamId);
        const needle = addMem.q.trim().toLowerCase();
        const cands = users
          .filter((u) => !(members[u.id] ?? []).some((m) => m.teamId === addMem.teamId))
          .filter((u) => !needle || `${u.name} ${u.email} ${u.position ?? ''}`.toLowerCase().includes(needle))
          .sort((a, b) => {
            // 어느 팀에도 없는 사람이 먼저 — 여기서 가장 먼저 처리해야 할 사람들이다.
            const ua = (members[a.id]?.length ?? 0) === 0 ? 0 : 1;
            const ub = (members[b.id]?.length ?? 0) === 0 ? 0 : 1;
            return (ua - ub) || a.name.localeCompare(b.name, 'ko');
          });
        return (
          <>
            <div className="um-scrim" onClick={() => setAddMem(null)} />
            <div className="um-dlg" role="dialog" aria-modal="true" aria-label="팀원 추가">
              <div className="um-dr__h">
                <span className="um-dr__t">
                  <b>팀원 추가</b>
                  <span>{t?.name ?? '팀'} 에 넣을 사람을 고르세요</span>
                </span>
                <button type="button" className="um-dr__x" aria-label="닫기" onClick={() => setAddMem(null)}><X size={15} /></button>
              </div>
              <div className="um-dr__b">
                <label className="um-search" style={{ width: '100%', marginBottom: 12 }}>
                  <Search size={14} />
                  <input value={addMem.q} autoFocus placeholder="이름 · 직위 · 이메일"
                    aria-label="사람 검색" onChange={(e) => setAddMem({ ...addMem, q: e.target.value })} />
                </label>
                <div className="um-od__cands">
                  {cands.length === 0 ? (
                    <p className="um-od__none">넣을 수 있는 사람이 없습니다.</p>
                  ) : cands.slice(0, 60).map((u) => {
                    const org = orgLabelOf(u);
                    return (
                      <button key={u.id} type="button" className="um-od__cand" disabled={bulkBusy}
                        onClick={() => { setAddMem(null); void moveUsersToTeam([u.id], addMem.teamId); }}>
                        <span className="um-av" style={hueStyle(org.hue)} aria-hidden="true">{initial(u.name, u.email)}</span>
                        <span style={{ minWidth: 0 }}>
                          <span className="um-id"><b>{u.name || '(이름 없음)'}</b>{u.position && <span className="um-od__pos">{u.position}</span>}</span>
                          <span className="um-meta"><span className="org" style={hueStyle(org.hue)}><i />{org.text}</span></span>
                        </span>
                        <Plus size={15} />
                      </button>
                    );
                  })}
                  {cands.length > 60 && <p className="um-f__hint">{cands.length - 60}명 더 있습니다 — 검색으로 좁히세요.</p>}
                </div>
              </div>
              <div className="um-dr__f">
                <button type="button" className="um-btn" onClick={() => setAddMem(null)}>닫기</button>
                <span className="sp" />
                <span className="um-f__hint">다른 팀에 있던 사람은 이 팀으로 <b>옮겨집니다</b>.</span>
              </div>
            </div>
          </>
        );
      })()}

      {renderMenu()}

      {drawer && (
        <UserDrawer
          isOrgMode={isOrgMode}
          key={drawer.mode === 'edit' ? drawer.userId : 'new'}
          userId={drawer.mode === 'edit' ? drawer.userId : null}
          teams={teams}
          memberships={drawer.mode === 'edit' ? (members[drawer.userId] ?? []) : []}
          defaultTeamId={sel.kind === 'team' ? sel.id : ''}
          myRole={myRole}
          myUserId={myId}
          onClose={() => setDrawer(null)}
          onDone={(msg, tone) => { setDrawer(null); setNotice({ text: msg, tone }); void reload(); }}
          onLeader={(teamId, isLeader) => { if (drawer.mode === 'edit') void toggleLeader(drawer.userId, teamId, isLeader); }}
          onDetach={() => { if (drawer.mode === 'edit') { setDrawer(null); void detachUsers([drawer.userId]); } }}
          onAssign={(teamId) => { if (drawer.mode === 'edit') { setDrawer(null); void moveUsersToTeam([drawer.userId], teamId); } }}
        />
      )}
    </div>
    </>
  );
}

/* ═══════════════════ 상세 드로어 ═══════════════════ */

/**
 * ★★ **「메뉴」 탭을 뺐다** (2026-08-28 · 자리 재정의 판 4).
 *    메뉴 권한은 **부서 단위로만** 준다 — 개인별로 걸기 시작하면 「이 사람은 왜 보이지」를
 *    명부에서 한 명씩 뒤져야 한다. 대상은 셋이다: 대표(전체) · 부서(팀) · 부서·팀 없는 직원.
 *    거는 자리는 이 화면 왼쪽 트리에서 대상을 고른 뒤 오른쪽 「메뉴 권한」 탭이다.
 */
type DrawerTab = 'info' | 'org' | 'pw';

function UserDrawer({ userId, teams, memberships, defaultTeamId, myRole, myUserId, isOrgMode, onClose, onDone, onLeader, onDetach, onAssign }: {
  userId: string | null;
  teams: OrgTeam[];
  memberships: Membership[];
  defaultTeamId: string;
  /** 로그인한 사람의 역할 — 고를 수 있는 권한과 비밀번호 조작 가능 여부를 정한다. */
  myRole: string;
  myUserId: string | null;
  /** 조직도 화면에서 열렸는가 — 계정 화면에서는 직위·소속을 다루지 않는다(판 4). */
  isOrgMode: boolean;
  onClose: () => void;
  onDone: (msg: string, tone: 'ok' | 'warn') => void;
  onLeader: (teamId: string, isLeader: boolean) => void;
  onDetach: () => void;
  onAssign: (teamId: string) => void;
}) {
  const isNew = userId === null;
  const [tab, setTab] = useState<DrawerTab>('info');
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // 정보 폼
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [empNo, setEmpNo] = useState('');
  const [position, setPosition] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<AssignableRole>('STAFF');
  const [active, setActive] = useState(true);
  // 등록 전용
  const [pwMode, setPwMode] = useState<PasswordMode>('invite_link');
  /**
   * ★ 등록 화면에서 **고르지는 않는다**(판 4) — 소속은 인사가 넣는다.
   *   다만 조직도에서 팀을 고른 채 «사용자 등록» 을 열었다면 그 팀으로 넣어 준다
   *   (거기서는 「이 팀에 사람을 넣는 중」 이라는 맥락이 분명하다).
   */
  const newTeam = isOrgMode ? defaultTeamId : '';
  const [invite, setInvite] = useState<CreateUserResponse['invite'] | null>(null);
  // 비밀번호 탭
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [resetOut, setResetOut] = useState<{ kind: PasswordMode; token?: string; tempPassword?: string } | null>(null);
  // 소속 탭
  const [addTeam, setAddTeam] = useState('');

  useEffect(() => {
    const uid = userId;
    if (uid === null) return;
    let alive = true;
    (async () => {
      try {
        const d = await getAdminUserDetail(uid);
        if (!alive) return;
        setDetail(d);
        setName(d.name); setEmail(d.email); setEmpNo(d.employeeNumber ?? '');
        setPosition(d.position ?? ''); setPhone(d.phone ?? '');
        setActive(d.isActive);
        // AI_BOT 만 등급 밖 — 나머지는 현재 권한을 그대로 싣는다(관리자·시스템관리자 포함).
        if (d.role !== 'AI_BOT') setRole(d.role as AssignableRole);
      } catch (e) {
        if (alive) setErr(errMsg(e, '사용자 정보를 불러오지 못했습니다.'));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isSelf = detail != null && myUserId != null && detail.id === myUserId;
  /**
   * 콘솔 등급(시스템관리자·매니저)을 달고 있는 사람 — **여기서는 손대지 않는다.**
   *
   * ★★ 2026-08-25 실제로 사고가 났다. 안내 문구는 "여기서는 바꾸지 않습니다" 라고 하는데
   *    **셀렉트는 열려 있어서**, 시스템관리자 계정이 이 화면에서 관리자로 내려갔고 그 순간
   *    관리 콘솔에 못 들어가게 됐다(콘솔은 시스템관리자만 들어온다 — #672).
   *    게다가 이 화면의 선택지는 관리자·직원뿐이라 **되돌릴 수도 없다** — 다른 시스템관리자가
   *    없었다면 콘솔이 통째로 잠겼다.
   *    말과 동작을 맞춘다: 콘솔 등급이면 셀렉트를 **잠근다**. 바꾸는 자리는 콘솔 › 계정 관리다.
   */
  const consoleGradeOnly = detail != null && !COMPANY_ASSIGNABLE_ROLES.includes(detail.role as ManagedRole);
  /** 권한을 바꿀 수 있는 대상인가 — 본인 제외 + 자기 등급 이하 + 콘솔 등급이 아닐 것(서버가 최종 판정). */
  const assignable = !detail || (!isSelf && !consoleGradeOnly && canManageRole(myRole, detail.role));
  /** 비밀번호는 권한과 같은 규칙. 본인은 내 계정 설정에서 바꾼다. */
  const canPassword = detail != null && !isSelf && canManageRole(myRole, detail.role);
  /**
   * 셀렉트 항목 — 내가 줄 수 있는 권한 + (바꿀 수 없더라도) 대상의 현재 권한.
   * 현재 값을 빼면 셀렉트가 빈칸으로 보여 "권한 없음" 처럼 읽힌다.
   */
  const roleOptions = useMemo<ManagedRole[]>(() => {
    // 내 등급으로 줄 수 있는 것 ∩ 회사 화면이 다루는 것. 대상의 현재 권한은 콘솔 등급이라도 남긴다.
    const mine = assignableRolesFor(myRole).filter((r) => COMPANY_ASSIGNABLE_ROLES.includes(r));
    const cur = detail?.role;
    return ROLE_ORDER.filter((r) => mine.includes(r) || r === cur);
  }, [myRole, detail?.role]);

  async function save() {
    setBusy(true); setErr(null); setMsg(null);
    try {
      if (isNew) {
        if (!email.trim() || !name.trim()) { setErr('이름과 email 은 필수입니다.'); setBusy(false); return; }
        const r = await createUser({
          email: email.trim(), name: name.trim(), role,
          position: position.trim() || undefined,
          phone: phone.trim() || undefined,
          employeeNumber: empNo.trim() || undefined,
          passwordMode: pwMode,
        });
        setInvite(r.invite);
        if (newTeam) { try { await addTeamMember(newTeam, { userId: r.user.id }); } catch { /* 배정 실패는 등록을 되돌리지 않는다 */ } }
        setMsg(`${r.user.name} 계정을 만들었습니다. 아래 초대 정보를 전달하세요.`);
        setBusy(false);
        return;
      }
      if (userId === null) { setBusy(false); return; }
      await updateAdminUserProfile(userId, {
        name: name.trim(), email: email.trim(),
        employeeNumber: empNo.trim() || null,
        position: position.trim() || null,
        phone: phone.trim() || null,
        isActive: active,
      });
      if (assignable && detail && detail.role !== role) await changeUserRole(userId, role);
      onDone(`${name} 님의 정보를 저장했습니다.`, 'ok');
    } catch (e) {
      setErr(errMsg(e, '저장에 실패했습니다.'));
      setBusy(false);
    }
  }

  async function applyPassword() {
    if (!userId) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await setAdminUserPassword(userId, pw);
      setPw(''); setMsg('비밀번호를 지정했습니다. 기존 세션은 모두 만료됐습니다.');
    } catch (e) { setErr(errMsg(e, '비밀번호 지정에 실패했습니다.')); }
    finally { setBusy(false); }
  }

  async function doReset(mode: PasswordMode) {
    if (!userId) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await resetAdminUserPassword(userId, mode);
      setResetOut(r.invite);
      setMsg('비밀번호를 초기화했습니다. 아래 정보를 전달하세요.');
    } catch (e) { setErr(errMsg(e, '초기화에 실패했습니다.')); }
    finally { setBusy(false); }
  }

  function copy(text: string) {
    void navigator.clipboard.writeText(text).then(
      () => setMsg('클립보드에 복사했습니다.'),
      () => setErr('복사에 실패했습니다. 직접 선택해 복사하세요.'),
    );
  }

  const teamLabel = (t: OrgTeam) => (t.divisionName ? `${t.divisionName} · ${t.name}` : `${t.name} (부서 없음)`);
  const hue = memberships.length ? hueOf(teams.find((t) => t.id === memberships[0]!.teamId)?.divisionId ?? memberships[0]!.teamId) : 'var(--text-subtle)';

  return (
    <>
      <div className="um-scrim" onClick={onClose} />
      <aside className="um-dr" role="dialog" aria-modal="true" aria-label={isNew ? '사용자 등록' : `${name} 인사정보`}>
        <div className="um-dr__h">
          <span className="um-av" style={hueStyle(hue)} aria-hidden="true">{isNew ? '+' : initial(name, email)}</span>
          <span className="um-dr__t">
            <b>{isNew ? '사용자 등록' : (name || '(이름 없음)')}</b>
            <span>{isNew ? '등록과 동시에 로그인 계정이 만들어집니다' : email}</span>
          </span>
          {/* ★ 여기도 `status`(접속)가 아니라 `isActive` 다 — 상세 응답도 접속 상태를 준다. */}
          {!isNew && detail && (
            <span className={`um-st um-st--${detail.isActive ? 'active' : 'disabled'}`}>
              {detail.isActive ? '활성' : '비활성'}
            </span>
          )}
          <button type="button" className="um-dr__x" aria-label="닫기" onClick={onClose}><X size={15} /></button>
        </div>

        {!isNew && (
          <div className="um-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'info'} className={tab === 'info' ? 'is-on' : ''} onClick={() => setTab('info')}>정보</button>
            {/* ★ 「소속」은 조직도에서만 (판 4) — 계정 화면은 계정만 다룬다.
                사람의 소속을 옮기는 일은 인사 › 직원 관리 · 인사발령이 한다. */}
            {isOrgMode && (
              <button type="button" role="tab" aria-selected={tab === 'org'} className={tab === 'org' ? 'is-on' : ''} onClick={() => setTab('org')}>소속</button>
            )}
            <button type="button" role="tab" aria-selected={tab === 'pw'} className={tab === 'pw' ? 'is-on' : ''} onClick={() => setTab('pw')}>비밀번호</button>
          </div>
        )}

        <div className="um-dr__b">
          {err && <div className="um-alert" role="alert" style={{ margin: '0 0 14px' }}><AlertTriangle size={15} /><span>{err}</span></div>}
          {msg && <div className="um-alert um-alert--ok" role="status" style={{ margin: '0 0 14px' }}><Check size={15} /><span>{msg}</span></div>}

          {loading ? (
            <div className="um-fields">
              {Array.from({ length: 6 }, (_, i) => <div className="um-f" key={i}><span className="um-skel" style={{ width: 60 }} /><span className="um-skel" style={{ height: 34, borderRadius: 9 }} /></div>)}
            </div>
          ) : (isNew || tab === 'info') ? (
            <>
              <div className="um-fields">
                <div className="um-f"><label htmlFor="um-name">이름<em>*</em></label>
                  <input id="um-name" className="um-inp" value={name} onChange={(e) => setName(e.target.value)} /></div>
                <div className="um-f"><label htmlFor="um-empno">사번</label>
                  <input id="um-empno" className="um-inp" value={empNo} onChange={(e) => setEmpNo(e.target.value)} />
                  <span className="um-f__hint">사번 로그인·임시비번 발급에 쓰입니다</span></div>
                <div className="um-f w2"><label htmlFor="um-email">email<em>*</em></label>
                  <input id="um-email" className="um-inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                  <span className="um-f__hint">회사 도메인만 허용됩니다</span></div>
                {/* ★ 직위는 계정이 아니다 (판 4) — 인사 › 직원 관리 · 인사발령이 정한다.
                    조직도 화면에서는 사람을 볼 때 함께 보이는 편이 낫다. */}
                {isOrgMode && (
                  <div className="um-f"><label htmlFor="um-pos">직위</label>
                    {/* 직위는 마스터에서 고른다(판 2) — 목록 밖 값은 막지 않고 알린다. */}
                    <JobGradeSelect
                      id="um-pos" className="um-inp" chevron manage
                      value={position} onChange={setPosition}
                    /></div>
                )}
                <div className="um-f"><label htmlFor="um-phone">연락처</label>
                  <input id="um-phone" className="um-inp" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
                <div className="um-f"><label htmlFor="um-role">권한</label>
                  <span className="um-selw" style={{ width: '100%' }}>
                    <select id="um-role" className="um-inp" value={role} disabled={!assignable}
                      onChange={(e) => setRole(e.target.value as AssignableRole)}>
                      {roleOptions.map((r) => <option key={r} value={r}>{ROLE_OPTION_LABEL[r]}</option>)}
                    </select>
                    <ChevronRight size={12} style={{ transform: 'rotate(90deg)' }} />
                  </span>
                  {consoleGradeOnly ? (
                    <span className="um-f__hint um-warn">
                      {ROLE_LABEL[detail!.role] ?? detail!.role} 은 <b>관리 콘솔의 계정 등급</b>입니다 —
                      여기서는 바꾸지 않습니다. 관리 콘솔 › 계정 관리에서 다룹니다.
                    </span>
                  ) : !assignable ? (
                    <span className="um-f__hint">
                      {isSelf ? '본인의 권한은 바꿀 수 없습니다' : '자신보다 상위 권한 계정의 권한은 바꿀 수 없습니다'}
                    </span>
                  ) : isAdminRole(role) ? (
                    <span className="um-f__hint">관리 콘솔에 접속할 수 있는 권한입니다</span>
                  ) : null}</div>
                {isNew ? (
                  <>
                    <div className="um-f"><label htmlFor="um-pwmode">초기 비밀번호</label>
                      <span className="um-selw" style={{ width: '100%' }}>
                        <select id="um-pwmode" className="um-inp" value={pwMode} onChange={(e) => setPwMode(e.target.value as PasswordMode)}>
                          <option value="invite_link">초대 링크</option>
                          <option value="temp_password">임시 비밀번호 (사번 필요)</option>
                        </select>
                        <ChevronRight size={12} style={{ transform: 'rotate(90deg)' }} />
                      </span></div>
                    {/* ★ 소속은 여기서 안 정한다 (판 4) — 계정을 만들면 **인사가 이어받는다**.
                        인사 홈의 «인사 정보 없는 사람» 이 그 후처리 큐다. */}
                    <div className="um-f w2">
                      <p className="um-f__hint">
                        부서 · 팀 · 직위 · 입사일은 <b>인사 › 직원 관리</b>에서 넣습니다 —
                        계정을 만들면 그쪽 «처리 대기» 에 뜹니다.
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="um-f w2">
                    <label className="um-chk" style={{ fontSize: '0.8rem' }}>
                      <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> 계정 활성
                    </label>
                    <span className="um-f__hint">끄면 로그인이 차단됩니다. 본인·관리자 계정은 바꿀 수 없습니다.</span>
                  </div>
                )}
              </div>

              {invite && (
                <>
                  <div className="um-secl">초대 정보</div>
                  <div className="um-row2">
                    <div className="um-code" style={{ flex: 1 }}>{invite.tempPassword ?? invite.token ?? '—'}</div>
                    <button type="button" className="um-btn um-btn--sm um-btn--icon"
                      aria-label="복사" onClick={() => copy(invite.tempPassword ?? invite.token ?? '')}><Copy size={14} /></button>
                  </div>
                  <p className="um-f__hint" style={{ marginTop: 6 }}>만료 {formatDateTime(invite.expiresAt)}</p>
                </>
              )}
            </>
          ) : tab === 'org' ? (
            <>
              <div className="um-secl">현재 소속</div>
              {memberships.length === 0 ? (
                <p className="um-f__hint">어느 팀에도 속해 있지 않습니다. 아래에서 팀을 지정하세요.</p>
              ) : memberships.map((m) => {
                const t = teams.find((x) => x.id === m.teamId);
                return (
                  <div className="um-row2" key={m.teamId} style={{ marginBottom: 8 }}>
                    <span className="um-code" style={{ flex: 1 }}>{t ? teamLabel(t) : m.teamId}</span>
                    <button type="button" className="um-btn um-btn--sm" onClick={() => onLeader(m.teamId, !m.isLeader)}>
                      {m.isLeader ? '팀장 해제' : '팀장 지정'}
                    </button>
                  </div>
                );
              })}
              <div className="um-secl">소속 변경</div>
              <div className="um-row2">
                <span className="um-selw" style={{ flex: 1 }}>
                  <select className="um-inp" aria-label="이동할 팀" value={addTeam} onChange={(e) => setAddTeam(e.target.value)}>
                    <option value="">팀 선택…</option>
                    {teams.map((t) => <option key={t.id} value={t.id}>{teamLabel(t)}</option>)}
                  </select>
                  <ChevronRight size={12} style={{ transform: 'rotate(90deg)' }} />
                </span>
                <button type="button" className="um-btn um-btn--primary um-btn--sm" disabled={!addTeam} onClick={() => onAssign(addTeam)}>이동</button>
              </div>
              <p className="um-f__hint" style={{ marginTop: 8 }}>
                기존 팀에서 빼고 선택한 팀으로 옮깁니다. 부서는 팀을 통해서만 연결되므로 부서 직접 배정은 없습니다.
              </p>
              {memberships.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <button type="button" className="um-btn um-btn--sm um-btn--danger" onClick={onDetach}>모든 소속에서 제외</button>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="um-secl">비밀번호 직접 지정</div>
              <div className="um-row2">
                <input className="um-inp" type={showPw ? 'text' : 'password'} value={pw} placeholder="8자 이상 · 영문·숫자·특수문자 3종"
                  aria-label="새 비밀번호" disabled={!canPassword} onChange={(e) => setPw(e.target.value)} />
                <button type="button" className="um-btn um-btn--sm um-btn--icon" aria-label={showPw ? '숨기기' : '보기'}
                  onClick={() => setShowPw((v) => !v)}>{showPw ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                <button type="button" className="um-btn um-btn--sm um-btn--primary" disabled={!pw || busy || !canPassword} onClick={() => void applyPassword()}>
                  <Key size={13} /> 지정
                </button>
              </div>
              <p className="um-f__hint" style={{ marginTop: 6 }}>
                {canPassword
                  ? '지정 즉시 로그인 가능해지고 기존 세션은 전부 만료됩니다.'
                  : isSelf
                    ? '본인 비밀번호는 내 계정 설정에서 현재 비밀번호를 확인한 뒤 바꿉니다.'
                    : '자신보다 상위 권한 계정의 비밀번호는 바꿀 수 없습니다.'}
              </p>

              <div className="um-secl">초기화</div>
              <div className="um-row2">
                <button type="button" className="um-btn um-btn--sm" disabled={busy || !canPassword} onClick={() => void doReset('invite_link')}>
                  <RotateCcw size={13} /> 초대 링크 재발급
                </button>
                <button type="button" className="um-btn um-btn--sm" disabled={busy || !canPassword} onClick={() => void doReset('temp_password')}>
                  <RotateCcw size={13} /> 임시 비밀번호
                </button>
              </div>
              {resetOut && (
                <div className="um-row2" style={{ marginTop: 10 }}>
                  <div className="um-code" style={{ flex: 1 }}>{resetOut.tempPassword ?? resetOut.token ?? '—'}</div>
                  <button type="button" className="um-btn um-btn--sm um-btn--icon" aria-label="복사"
                    onClick={() => copy(resetOut.tempPassword ?? resetOut.token ?? '')}><Copy size={14} /></button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="um-dr__f">
          {(isNew || tab === 'info') && !invite && (
            <button type="button" className="um-btn um-btn--primary" disabled={busy || loading} onClick={() => void save()}>
              {busy ? '처리 중…' : (isNew ? '등록' : '저장')}
            </button>
          )}
          {invite && (
            <button type="button" className="um-btn um-btn--primary" onClick={() => onDone('사용자를 등록했습니다.', 'ok')}>완료</button>
          )}
          <button type="button" className="um-btn" onClick={onClose}>닫기</button>
          <span className="sp" />
        </div>
      </aside>
    </>
  );
}
